import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, Activity, ClipboardCheck, Package, Truck, RotateCcw, Clock, FileText, LayoutGrid, Scissors, CheckSquare, Archive, Plus, X, Inbox, AlertTriangle, Layers, Check, Search, Paperclip, History, Trash2 } from "lucide-react";
import JsBarcode from "jsbarcode";
import { MAROON, inpL } from "@/components/kiyana/LeaveForm";
import { usePager, Pager } from "@/components/kiyana/Pager";

const card = "bg-white rounded-2xl border border-slate-100";

const STAGES = [
  { id: "monitoring", label: "Monitoring", icon: Activity },
  { id: "cutting", label: "Potong & Bundel", icon: LayoutGrid },
  { id: "sewing", label: "Jahit", icon: Scissors },
  { id: "qc", label: "Finishing & QC", icon: CheckSquare },
  { id: "warehouse", label: "Gudang", icon: Archive },
];

const BUNDLE_STATUS = { ready_jahit: ["Ready Jahit", "bg-emerald-50 text-emerald-600"], dijahit: ["Dijahit", "bg-sky-50 text-sky-600"], qc_sebagian: ["QC Sebagian", "bg-amber-50 text-amber-600"], selesai_qc: ["Selesai QC", "bg-teal-50 text-teal-600"], selesai: ["Selesai", "bg-slate-100 text-slate-500"] };

function Modal({ tid, title, onClose, children, wide, xl }) {
  return (
    <div data-testid={tid} className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div className={`bg-white w-full ${xl ? "sm:max-w-5xl" : wide ? "sm:max-w-2xl" : "sm:max-w-lg"} rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="font-display text-base font-bold text-slate-900">{title}</h3>
          <button data-testid={`${tid}-close`} onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

/* ---------------- SPK POTONG ---------------- */
function SpkForm({ options, onClose, onSaved }) {
  const [form, setForm] = useState({ product_id: "", variants: [], assigned_to: "", location: "", deadline: "", instructions: "", materials: [], attachment_url: null, attachment_name: "" });
  const [prodQ, setProdQ] = useState("");
  const [prodOpen, setProdOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const costProds = options.cost_products || [];
  const prod = options.products.find((p) => p.id === form.product_id);
  const selectedCostProd = costProds.find((p) => p.product_id === form.product_id);
  const prodOpts = costProds.filter((p) => p.name.toLowerCase().includes(prodQ.toLowerCase()));
  const cutters = options.employees.filter((e) => e.role === "cutting");
  const assigneeOpts = cutters;
  const colors = [...new Set((prod?.variants || []).map((v) => v.label.split(" / ")[0]))];
  const sizesFor = (color) => [...new Set((prod?.variants || []).filter((v) => v.label.split(" / ")[0] === color).map((v) => v.label.split(" / ")[1] || "-"))];
  const usedLabels = form.variants.map((v) => `${v.color} / ${v.size}`);
  const totalTarget = form.variants.reduce((s, v) => s + (parseInt(v.target_qty) || 0), 0);
  const bahanSummary = Object.values(form.materials.reduce((acc, m) => {
    if (!m.material_id) return acc;
    const mat = options.materials.find((x) => x.id === m.material_id);
    const key = `${m.material_id}|${m.color_lot || ""}`;
    if (!acc[key]) acc[key] = { name: mat?.name || "", color_lot: m.color_lot || "", qty: 0, unit: mat?.unit || "" };
    acc[key].qty += parseInt(m.qty) || 0;
    return acc;
  }, {}));

  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { color: "", size: "", target_qty: "" }] }));
  const setVarRow = (i, patch) => setForm((f) => { const vs = [...f.variants]; vs[i] = { ...vs[i], ...patch }; return { ...f, variants: vs }; });
  const addMat = () => setForm((f) => ({ ...f, materials: [...f.materials, { material_id: "", color_lot: "", for_variants: "", qty: "" }] }));
  const setMatRow = (i, patch) => setForm((f) => { const ms = [...f.materials]; ms[i] = { ...ms[i], ...patch }; return { ...f, materials: ms }; });

  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/uploads", fd); setForm((s) => ({ ...s, attachment_url: data.url, attachment_name: f.name })); toast.success("Lampiran terunggah"); }
    catch { toast.error("Gagal mengunggah lampiran"); }
  };

  const submit = async (publish) => {
    if (!form.product_id) return toast.error("Pilih Produk Produksi terlebih dahulu");
    if (!form.variants.length || form.variants.some((v) => !v.color || !v.size || !(parseInt(v.target_qty) > 0))) return toast.error("Lengkapi variasi & target qty");
    if (!form.deadline) return toast.error("Deadline wajib diisi");
    if (!form.assigned_to) return toast.error("Pilih karyawan penerima SPK");
    setBusy(true);
    try {
      await api.post("/admin/prod/spk", {
        product_id: form.product_id,
        variants: form.variants.map((v) => ({ variant: `${v.color} / ${v.size}`, target_qty: parseInt(v.target_qty) })),
        assigned_to: form.assigned_to, location: form.location, deadline: form.deadline,
        instructions: form.instructions, attachment_url: form.attachment_url, attachment_name: form.attachment_name || null,
        materials: form.materials.filter((m) => m.material_id && parseInt(m.qty) > 0)
          .map((m) => ({ material_id: m.material_id, qty: parseInt(m.qty), color_lot: m.color_lot, for_variants: m.for_variants })),
        publish,
      });
      toast.success(publish ? "SPK diterbitkan ke tim cutting" : "SPK disimpan sebagai draft — belum dikirim ke tim cutting");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan SPK"); } finally { setBusy(false); }
  };
  return (
    <Modal tid="spkf-modal" title="Buat SPK Potong" onClose={onClose} xl>
      <p className="text-[11px] text-slate-400 -mt-1">Buat permintaan potong dan tentukan bahan untuk tim cutting. Nomor SPK dibuat otomatis saat disimpan.</p>
      <div className="grid lg:grid-cols-5 gap-4 items-start">
        <div className="lg:col-span-3 space-y-4">
          <div className="border border-slate-100 rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center"><FileText className="w-4 h-4" /></span><span className="font-display font-bold text-sm text-slate-900">Informasi SPK</span></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Nomor SPK</label>
                <div className="h-10 px-3 rounded-xl bg-slate-50 border border-slate-100 text-xs flex items-center text-slate-400 font-mono">Otomatis (PP-…)</div>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Produk Produksi <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="spkf-costprod-q" value={form.product_id ? (selectedCostProd?.name || prod?.name || "") : prodQ}
                    onFocus={() => { setProdQ(""); setForm((f) => ({ ...f, product_id: "", variants: [] })); setProdOpen(true); }}
                    onClick={() => { setProdQ(""); setForm((f) => ({ ...f, product_id: "", variants: [] })); setProdOpen(true); }}
                    onBlur={() => setTimeout(() => setProdOpen(false), 250)}
                    onChange={(e) => { setProdQ(e.target.value); setForm({ ...form, product_id: "", variants: [] }); setProdOpen(true); }}
                    placeholder="Klik untuk memilih Produk Produksi..." className={`${inpL} pl-9`} />
                </div>
                <p className="text-[9px] text-slate-400 mt-0.5">Sumber: Master Produk Produksi (HPP)</p>
                {prodOpen && !form.product_id && (
                  <div data-testid="spkf-costprod-list" className="mt-1 border-2 border-[#7F1D2D]/30 rounded-xl overflow-hidden max-h-44 overflow-y-auto bg-white shadow-lg">
                    {costProds.length === 0 && <p className="px-3 py-3 text-[10px] text-slate-400">Belum ada Produk Produksi aktif — buat rincian HPP dulu di menu Produk Produksi.</p>}
                    {prodOpts.slice(0, 15).map((p) => (
                      <button key={p.product_id} data-testid={`spkf-costprod-${p.product_id}`}
                        onMouseDown={(e) => { e.preventDefault(); setForm({ ...form, product_id: p.product_id, variants: [] }); setProdOpen(false); }}
                        className="w-full text-left px-3 py-2.5 text-xs hover:bg-rose-50/40 active:bg-rose-50 text-slate-700 border-b border-slate-50 last:border-0">
                        {p.name} <span className="text-slate-400">• {p.hpp_variants.length} variasi ber-HPP</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Deadline <span className="text-red-500">*</span></label>
                <input data-testid="spkf-deadline" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className={inpL} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Lokasi Tim Cutting</label>
                <input data-testid="spkf-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="mis. Konveksi Ciwarengin" className={inpL} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Role Penerima</label>
                <div className="h-10 px-3 rounded-xl bg-slate-50 border border-slate-100 text-xs flex items-center text-slate-400">Cutting / Potong</div>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500">Karyawan Penerima <span className="text-red-500">*</span></label>
                <select data-testid="spkf-assignee" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className={inpL}>
                  <option value="">Pilih karyawan cutting</option>
                  {assigneeOpts.map((e) => <option key={e.id} value={e.id}>{`${e.name} (${e.role_label})`}</option>)}
                </select>
                {!cutters.length && <p className="text-[10px] text-amber-600 mt-1">Belum ada karyawan role Cutting — atur role di menu Karyawan.</p>}
              </div>
            </div>
          </div>

          <div className="border border-slate-100 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center"><LayoutGrid className="w-4 h-4" /></span><span className="font-display font-bold text-sm text-slate-900">Variasi & Target Produksi</span></div>
              <button data-testid="spkf-add-variant" onClick={addVariant} disabled={!form.product_id} className="h-8 px-3 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40"><Plus className="w-3 h-3" /> Tambah Variasi</button>
            </div>
            {form.variants.map((v, i) => (
              <div key={i} className="flex gap-1.5 items-center">
                <select data-testid={`spkf-v-color-${i}`} value={v.color} onChange={(e) => setVarRow(i, { color: e.target.value, size: "" })} className={inpL}>
                  <option value="">Warna</option>
                  {colors.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select data-testid={`spkf-v-size-${i}`} value={v.size} onChange={(e) => setVarRow(i, { size: e.target.value })} className={inpL} disabled={!v.color}>
                  <option value="">Ukuran</option>
                  {sizesFor(v.color).map((s) => { const lb = `${v.color} / ${s}`; return <option key={s} value={s} disabled={usedLabels.includes(lb)}>{s}</option>; })}
                </select>
                <input data-testid={`spkf-v-qty-${i}`} type="number" min="1" value={v.target_qty} placeholder="Target" onChange={(e) => setVarRow(i, { target_qty: e.target.value })} className="h-10 w-24 px-2 rounded-xl border border-slate-200 text-xs" />
                <span className="text-[10px] text-slate-400 w-7">pcs</span>
                <button data-testid={`spkf-v-del-${i}`} onClick={() => setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }))} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!form.variants.length && <p className="text-[10px] text-slate-400">Pilih Produk Produksi lalu tambahkan variasi & targetnya.</p>}
            <div data-testid="spkf-var-total" className="text-[11px] font-semibold text-slate-600 bg-slate-50 rounded-lg px-3 py-2">Total: {form.variants.length} variasi • Total Target: {totalTarget.toLocaleString("id-ID")} pcs</div>
          </div>

          <div className="border border-slate-100 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center"><Layers className="w-4 h-4" /></span><span className="font-display font-bold text-sm text-slate-900">Bahan yang Diserahkan</span></div>
              <button data-testid="spkf-add-mat" onClick={addMat} className="h-8 px-3 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-[11px] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Tambah Bahan</button>
            </div>
            {form.materials.map((m, i) => {
              const mat = options.materials.find((x) => x.id === m.material_id);
              return (
                <div key={i} className="flex gap-1.5 items-center flex-wrap sm:flex-nowrap">
                  <select data-testid={`spkf-mat-${i}`} value={m.material_id} onChange={(e) => setMatRow(i, { material_id: e.target.value })} className={inpL}>
                    <option value="">Bahan</option>
                    {options.materials.map((x) => <option key={x.id} value={x.id}>{`${x.name} (stok ${x.stock} ${x.unit})`}</option>)}
                  </select>
                  <input data-testid={`spkf-mat-lot-${i}`} value={m.color_lot} placeholder="Warna / LOT" onChange={(e) => setMatRow(i, { color_lot: e.target.value })} className="h-10 w-32 px-2 rounded-xl border border-slate-200 text-xs" />
                  <input data-testid={`spkf-mat-for-${i}`} value={m.for_variants} placeholder="Untuk variasi" onChange={(e) => setMatRow(i, { for_variants: e.target.value })} className="h-10 w-28 px-2 rounded-xl border border-slate-200 text-xs" />
                  <input data-testid={`spkf-mat-qty-${i}`} type="number" min="1" value={m.qty} placeholder="Jumlah" onChange={(e) => setMatRow(i, { qty: e.target.value })} className="h-10 w-20 px-2 rounded-xl border border-slate-200 text-xs" />
                  <span className="text-[10px] text-slate-400 w-8">{mat?.unit || ""}</span>
                  <button onClick={() => setForm((f) => ({ ...f, materials: f.materials.filter((_, j) => j !== i) }))} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
            {!form.materials.length && <p className="text-[10px] text-slate-400">Belum ada bahan — tambahkan bila SPK membutuhkan penyerahan bahan baku.</p>}
            <div className="text-[10px] text-slate-400 flex items-center gap-2 flex-wrap">Status Penyerahan: <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">Belum Diserahkan</span> — stok berkurang saat penyerahan dikonfirmasi.</div>
          </div>

          <div className="border border-slate-100 rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center"><FileText className="w-4 h-4" /></span><span className="font-display font-bold text-sm text-slate-900">Instruksi Pemotongan</span></div>
            <textarea data-testid="spkf-instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={2} placeholder="mis. Periksa pola dan pisahkan hasil potong per warna serta ukuran." className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#7F1D2D]" />
            <div className="flex items-center gap-2 flex-wrap">
              <Paperclip className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-[11px] font-semibold text-slate-500">Lampiran Pola / Foto (opsional)</span>
              <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={upload} />
              <button data-testid="spkf-attach" onClick={() => fileRef.current?.click()} className="h-9 px-3 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-[11px] font-semibold">{form.attachment_name || "Unggah File"}</button>
              <span className="text-[9px] text-slate-400">JPG, PNG, PDF. Maks 10 MB.</span>
            </div>
          </div>

          <p className="text-[10px] text-slate-400 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Draft belum mengirim pekerjaan ke tim cutting.</p>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="spkf-draft" onClick={() => submit(false)} disabled={busy} className="h-11 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-sm font-semibold disabled:opacity-40">Simpan Draft</button>
            <button data-testid="spkf-publish" onClick={() => submit(true)} disabled={busy} className="h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Terbitkan SPK"}</button>
          </div>
        </div>

        <div className="lg:col-span-2 border border-slate-100 rounded-2xl p-4 space-y-3 lg:sticky lg:top-4" data-testid="spkf-summary">
          <div className="flex items-center gap-2"><span className="w-7 h-7 rounded-lg bg-[#FBF3F4] text-[#7F1D2D] flex items-center justify-center"><FileText className="w-4 h-4" /></span><span className="font-display font-bold text-sm text-slate-900">Ringkasan SPK</span></div>
          <div className="text-xs space-y-1.5">
            <div className="flex justify-between gap-2"><span className="text-slate-400">Produk Produksi</span><b className="text-right">{selectedCostProd?.name || prod?.name || "—"}</b></div>
            <div className="flex justify-between"><span className="text-slate-400">Total Variasi</span><b>{form.variants.length}</b></div>
            <div className="flex justify-between"><span className="text-slate-400">Total Target</span><b data-testid="spkf-sum-target">{totalTarget.toLocaleString("id-ID")} pcs</b></div>
          </div>
          <div className="border-t border-slate-100 pt-2">
            <div className="text-[10px] font-bold text-slate-500 mb-1">List Variasi & Target</div>
            {form.variants.map((v, i) => <div key={i} className="flex justify-between text-[11px] py-0.5"><span className="text-slate-600">{v.color || "—"} / {v.size || "—"}</span><b>{(parseInt(v.target_qty) || 0).toLocaleString("id-ID")} pcs</b></div>)}
            {!form.variants.length && <p className="text-[10px] text-slate-300">—</p>}
          </div>
          <div className="border-t border-slate-100 pt-2">
            <div className="text-[10px] font-bold text-slate-500 mb-1">Bahan Direncanakan</div>
            {bahanSummary.map((b, i) => <div key={i} className="flex justify-between text-[11px] py-0.5"><span className="text-slate-600">{b.name}{b.color_lot ? ` (${b.color_lot})` : ""}</span><b>{b.qty.toLocaleString("id-ID")} {b.unit}</b></div>)}
            {!bahanSummary.length && <p className="text-[10px] text-slate-300">—</p>}
          </div>
          <div className="text-[10px] text-sky-700 bg-sky-50 border border-sky-100 rounded-lg p-2">Hasil aktual, batch, dan bundel dibuat terpisah per variasi setelah cutting selesai.</div>
        </div>
      </div>
    </Modal>
  );
}

function HandoverModal({ spk, options, onClose, onSaved }) {
  const [rows, setRows] = useState(spk.materials.map((m) => ({ material_id: m.material_id, qty: String(m.qty) })));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/prod/spk/${spk.id}/handover`, { materials: rows.filter((r) => r.material_id && parseInt(r.qty) > 0).map((r) => ({ material_id: r.material_id, qty: parseInt(r.qty) })) });
      toast.success("Bahan diserahkan — stok bahan berkurang satu kali"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyerahkan bahan"); } finally { setBusy(false); }
  };
  return (
    <Modal tid="handover-modal" title={`Serahkan Bahan • ${spk.number}`} onClose={onClose}>
      <p className="text-[11px] text-slate-400">Stok bahan baku berkurang <b>tepat satu kali</b> saat penyerahan ini.</p>
      {rows.map((r, i) => {
        const mat = options.materials.find((x) => x.id === r.material_id);
        return (
          <div key={i} className="flex gap-2 items-center">
            <div className="flex-1 text-xs font-semibold text-slate-700">{mat?.name || r.material_id}{spk.materials?.[i]?.color_lot ? <span className="font-normal text-slate-400"> • {spk.materials[i].color_lot}</span> : null}</div>
            <input data-testid={`handover-qty-${i}`} type="number" min="1" value={r.qty} onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} className="h-10 w-24 px-3 rounded-xl bg-white border border-slate-200 text-xs" />
            <span className="text-[10px] text-slate-400 w-16">/ stok {mat?.stock} {mat?.unit}</span>
          </div>
        );
      })}
      <button data-testid="handover-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Memproses..." : "Serahkan Bahan"}</button>
    </Modal>
  );
}

function UsageModal({ spk, onClose, onSaved }) {
  const handed = spk.handover?.materials || [];
  const [rows, setRows] = useState(handed.map((m) => ({ material_id: m.material_id, used: String(m.qty), returned: "0" })));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/prod/spk/${spk.id}/usage`, { materials: rows.map((r) => ({ material_id: r.material_id, used: parseInt(r.used || 0), returned: parseInt(r.returned || 0) })) });
      toast.success("Pemakaian & pengembalian sisa tercatat"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat pemakaian"); } finally { setBusy(false); }
  };
  return (
    <Modal tid="usage-modal" title={`Pemakaian Aktual & Sisa • ${spk.number}`} onClose={onClose}>
      <p className="text-[11px] text-slate-400">Pemakaian + sisa harus sama dengan qty yang diserahkan. Hanya sisa yang kembali ke stok.</p>
      {rows.map((r, i) => {
        const h = handed.find((m) => m.material_id === r.material_id);
        return (
          <div key={i} className="space-y-1">
            <div className="text-xs font-semibold text-slate-700">{h?.name} <span className="font-normal text-slate-400">(diserahkan {h?.qty} {h?.unit})</span></div>
            <div className="flex gap-2">
              <input data-testid={`usage-used-${i}`} type="number" min="0" value={r.used} placeholder="Terpakai" onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, used: e.target.value } : x))} className="h-10 flex-1 px-3 rounded-xl bg-white border border-slate-200 text-xs" />
              <input data-testid={`usage-ret-${i}`} type="number" min="0" value={r.returned} placeholder="Sisa kembali" onChange={(e) => setRows((rs) => rs.map((x, j) => j === i ? { ...x, returned: e.target.value } : x))} className="h-10 flex-1 px-3 rounded-xl bg-white border border-slate-200 text-xs" />
            </div>
          </div>
        );
      })}
      <button data-testid="usage-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Simpan Pemakaian"}</button>
    </Modal>
  );
}

function HoldModal({ spk, onClose, onSaved }) {
  const [form, setForm] = useState({ reason: "", qty: "", photo_url: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/uploads", fd); setForm((s) => ({ ...s, photo_url: data.url })); toast.success("Foto terunggah"); }
    catch { toast.error("Gagal mengunggah foto"); }
  };
  const submit = async () => {
    if (form.reason.trim().length < 5) return toast.error("Alasan hold wajib diisi");
    if (!(parseInt(form.qty) > 0)) return toast.error("Qty hold wajib lebih dari 0");
    setBusy(true);
    try {
      await api.post(`/prod/spk/${spk.id}/hold`, { reason: form.reason.trim(), qty: parseInt(form.qty), photo_url: form.photo_url || null });
      toast.success("Pengajuan hold dikirim ke pengawas"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan hold"); } finally { setBusy(false); }
  };
  return (
    <Modal tid="hold-modal" title={`Ajukan Hold Produksi • ${spk.number}`} onClose={onClose}>
      <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Bagian yang di-hold tidak bisa dilanjutkan sampai diputuskan pengawas.
      </div>
      <div>
        <label className="text-[11px] font-semibold text-slate-500">Alasan (mis. bahan cacat) <span className="text-red-500">*</span></label>
        <textarea data-testid="hold-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs focus:outline-none focus:border-[#7F1D2D]" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Qty terdampak <span className="text-red-500">*</span></label>
          <input data-testid="hold-qty" type="number" min="1" max={spk.target_qty} value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className={inpL} />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Foto bukti</label>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
          <button data-testid="hold-photo" onClick={() => fileRef.current?.click()} className="w-full h-10 px-3 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500 hover:border-[#7F1D2D] flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> {form.photo_url ? "Foto terlampir" : "Unggah foto"}</button>
        </div>
      </div>
      <button data-testid="hold-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Mengirim..." : "Kirim Pengajuan Hold"}</button>
    </Modal>
  );
}

/* ---------------- POLA TAHAP: filter, statistik, tabel bersama ---------------- */
const STAT_TONES = { rose: "bg-[#FBF3F4] text-[#7F1D2D]", sky: "bg-sky-50 text-sky-600", amber: "bg-amber-50 text-amber-600", emerald: "bg-emerald-50 text-emerald-600" };

function StageStat({ icon: Icon, label, value, sub, tone = "rose" }) {
  return (
    <div className={`${card} p-4 flex items-start gap-3`}>
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${STAT_TONES[tone]}`}><Icon className="w-4 h-4" /></span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
        <div className="font-display text-xl font-bold text-slate-900 leading-tight">{value}</div>
        <div className="text-[10px] text-slate-400 truncate">{sub}</div>
      </div>
    </div>
  );
}

const EMPTY_FILTER = { period: "", spk: "", produk: "", variasi: "" };

function matchFilter(x, f, dateStr) {
  if (f.period && !(dateStr || "").startsWith(f.period)) return false;
  if (f.spk && x.spk_id !== f.spk) return false;
  if (f.produk && x.product_name !== f.produk) return false;
  if (f.variasi && x.variant !== f.variasi) return false;
  return true;
}

function StageFilters({ items, value, onChange, testid }) {
  const spks = [...new Map(items.map((x) => [x.spk_id, x.spk_number])).entries()].filter(([id]) => id);
  const prods = [...new Set(items.map((x) => x.product_name).filter(Boolean))];
  const vars = [...new Set(items.filter((x) => !value.produk || x.product_name === value.produk).map((x) => x.variant).filter(Boolean))];
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className={`${card} p-3 grid grid-cols-2 lg:grid-cols-5 gap-2`} data-testid={`${testid}-filters`}>
      <div>
        <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Periode</div>
        <input data-testid={`${testid}-f-period`} type="month" value={value.period} onChange={(e) => set("period", e.target.value)} className={inpL} />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">SPK</div>
        <select data-testid={`${testid}-f-spk`} value={value.spk} onChange={(e) => set("spk", e.target.value)} className={inpL}>
          <option value="">Semua SPK</option>
          {spks.map(([id, num]) => <option key={id} value={id}>{num}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Produk</div>
        <select data-testid={`${testid}-f-produk`} value={value.produk} onChange={(e) => onChange({ ...value, produk: e.target.value, variasi: "" })} className={inpL}>
          <option value="">Semua Produk</option>
          {prods.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Variasi</div>
        <select data-testid={`${testid}-f-variasi`} value={value.variasi} onChange={(e) => set("variasi", e.target.value)} className={inpL}>
          <option value="">Semua Variasi</option>
          {vars.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
      <div className="flex items-end">
        <button data-testid={`${testid}-f-reset`} onClick={() => onChange(EMPTY_FILTER)} className="h-10 px-4 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold flex items-center gap-1.5 w-full justify-center"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
      </div>
    </div>
  );
}

function SewerShareTable({ bundles, testid }) {
  const [q, setQ] = useState("");
  const [openSewer, setOpenSewer] = useState(null);
  const bySewer = new Map();
  for (const b of bundles) {
    if (b.parent_id || !b.sewer_id) continue;
    if (!bySewer.has(b.sewer_id)) bySewer.set(b.sewer_id, { name: b.sewer_name || "—", items: [] });
    bySewer.get(b.sewer_id).items.push(b);
  }
  const rows = [...bySewer.entries()].map(([sid, g]) => {
    const dibagikan = g.items.reduce((a, b) => a + b.qty, 0);
    const setor = g.items.reduce((a, b) => a + (b.qty - (b.ready_qty || 0)), 0);
    return { sid, name: g.name, items: g.items, bundel: g.items.length, dibagikan, setor, sisa: dibagikan - setor,
      prods: [...new Set(g.items.map((b) => `${b.product_name} • ${b.variant}`))] };
  }).filter((r) => r.name.toLowerCase().includes(q.trim().toLowerCase()));
  const tot = rows.reduce((a, r) => ({ bundel: a.bundel + r.bundel, dibagikan: a.dibagikan + r.dibagikan, setor: a.setor + r.setor, sisa: a.sisa + r.sisa }), { bundel: 0, dibagikan: 0, setor: 0, sisa: 0 });
  const initials = (n) => n.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div className={`${card} overflow-hidden`} data-testid={`${testid}-share`}>
      <div className="px-4 pt-4 pb-3 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="font-display font-bold text-slate-900 text-sm">Pembagian ke Penjahit</div>
          <div className="text-[10px] text-slate-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Dicatat mandor saat bundel diserahkan ke penjahit.</div>
        </div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid={`${testid}-share-q`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / ID penjahit" className="h-9 pl-9 pr-3 rounded-xl border border-slate-200 text-xs w-56 focus:outline-none focus:border-[#7F1D2D]" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-y border-slate-100 bg-slate-50/60">
            <th className="px-4 py-2">Penjahit</th><th className="px-3 py-2">Produk / Variasi</th><th className="px-3 py-2 text-center">Bundel</th>
            <th className="px-3 py-2 text-right">Dibagikan</th><th className="px-3 py-2 text-right">Sudah Setor</th><th className="px-3 py-2 text-center">Sisa di Penjahit</th><th className="px-4 py-2 text-right">Aksi</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.sid}>
                <tr data-testid={`${testid}-share-row-${r.sid}`} className="border-b border-slate-50">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-[#FBF3F4] text-[#7F1D2D] text-[10px] font-bold flex items-center justify-center shrink-0">{initials(r.name)}</span>
                      <div className="font-semibold text-slate-800">{r.name}</div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 text-[11px]">{r.prods.slice(0, 2).join(" | ")}{r.prods.length > 2 ? ` +${r.prods.length - 2}` : ""}</td>
                  <td className="px-3 py-2.5 text-center">{r.bundel}</td>
                  <td className="px-3 py-2.5 text-right font-semibold">{r.dibagikan.toLocaleString("id-ID")} pcs</td>
                  <td className="px-3 py-2.5 text-right">{r.setor.toLocaleString("id-ID")} pcs</td>
                  <td className="px-3 py-2.5 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${r.sisa > 0 ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>{r.sisa.toLocaleString("id-ID")} pcs</span></td>
                  <td className="px-4 py-2.5 text-right"><button data-testid={`${testid}-share-detail-${r.sid}`} onClick={() => setOpenSewer(openSewer === r.sid ? null : r.sid)} className="text-[11px] font-semibold text-[#7F1D2D]">Detail ›</button></td>
                </tr>
                {openSewer === r.sid && (
                  <tr className="bg-slate-50/50 border-b border-slate-100">
                    <td colSpan={7} className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        {r.items.map((b) => <span key={b.id} className="text-[10px] px-2 py-1 rounded-lg bg-white border border-slate-100 font-mono text-slate-600">{b.number} • {b.qty} pcs • sisa {b.ready_qty}</span>)}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {rows.length > 0 && (
              <tr className="bg-slate-50/60 font-bold text-slate-800">
                <td className="px-4 py-2.5" colSpan={2}>Total</td>
                <td className="px-3 py-2.5 text-center">{tot.bundel}</td>
                <td className="px-3 py-2.5 text-right">{tot.dibagikan.toLocaleString("id-ID")} pcs</td>
                <td className="px-3 py-2.5 text-right">{tot.setor.toLocaleString("id-ID")} pcs</td>
                <td className="px-3 py-2.5 text-center">{tot.sisa.toLocaleString("id-ID")} pcs</td>
                <td className="px-4 py-2.5" />
              </tr>
            )}
            {!rows.length && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Belum ada bundel yang dibagikan ke penjahit.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BatchRincianCard({ b }) {
  const [showAll, setShowAll] = useState(false);
  const bl = b.bundles || [];
  const readyPcs = bl.filter((x) => !x.parent_id && x.status === "ready_jahit" && !x.sewer_id).reduce((a, x) => a + (x.ready_qty || 0), 0);
  const dibagikan = bl.filter((x) => !x.parent_id && x.sewer_id).reduce((a, x) => a + x.qty, 0);
  const shown = showAll ? bl : bl.slice(0, 3);
  const chip = (x) => {
    if (x.parent_id) return x.status === "selesai_qc" ? ["Selesai QC", "bg-emerald-50 text-emerald-600", CheckSquare] : ["Sudah setor", "bg-emerald-50 text-emerald-600", Check];
    if (!x.sewer_id && x.status === "ready_jahit") return ["Ready jahit", "bg-sky-50 text-sky-600", Package];
    if (x.sewer_id && (x.ready_qty || 0) > 0) return ["Di penjahit", "bg-amber-50 text-amber-600", Clock];
    return ["Sudah setor", "bg-emerald-50 text-emerald-600", Check];
  };
  return (
    <div data-testid={`batch-${b.id}`} className={`${card} overflow-hidden`}>
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap border-b border-slate-100">
        <span className="text-[10px] px-2.5 py-1 rounded-lg font-mono font-bold text-white" style={{ background: MAROON }}>{b.number}</span>
        <span className="text-xs font-semibold text-slate-700">{b.product_name} • {b.variant}</span>
        <span className="text-[10px] text-slate-300">|</span>
        <span className="text-[11px] text-slate-500">{b.total_qty?.toLocaleString("id-ID")} pcs • {b.bundle_count} bundel</span>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {b.diff_from_target !== 0 && <span data-testid={`batch-diff-${b.id}`} className="text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">Hasil {b.total_qty?.toLocaleString("id-ID")} vs target {b.target_qty?.toLocaleString("id-ID")}</span>}
          <span data-testid={`batch-ready-${b.id}`} className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 font-semibold inline-flex items-center gap-1"><Package className="w-3 h-3" /> Ready {readyPcs.toLocaleString("id-ID")} pcs</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold inline-flex items-center gap-1"><Truck className="w-3 h-3" /> Dibagikan {dibagikan.toLocaleString("id-ID")} pcs</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50/60 text-left text-[10px] uppercase text-slate-400">
            <th className="px-4 py-2">No. Bundel</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Penjahit</th><th className="px-4 py-2">Status</th>
          </tr></thead>
          <tbody>
            {shown.map((x) => {
              const [lbl, cls, Ico] = chip(x);
              return (
                <tr key={x.id} data-testid={`bundle-row-${x.id}`} className="border-t border-slate-50">
                  <td className="px-4 py-2 font-mono font-semibold text-slate-700">{x.number}{x.parent_id && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 font-sans">pecahan</span>}</td>
                  <td className="px-3 py-2">{x.qty?.toLocaleString("id-ID")} pcs</td>
                  <td className="px-3 py-2 text-slate-600">{x.sewer_name || "Belum dibagikan"}</td>
                  <td className="px-4 py-2"><span data-testid={`bundle-status-${x.id}`} className={`text-[9px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap inline-flex items-center gap-1 ${cls}`}><Ico className="w-3 h-3" /> {lbl}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {bl.length > 3 && (
        <button data-testid={`batch-more-${b.id}`} onClick={() => setShowAll(!showAll)} className="px-4 py-2.5 text-[11px] font-semibold text-[#7F1D2D]">
          {showAll ? "Tutup sebagian" : `Lihat semua ${bl.length} bundel ›`}
        </button>
      )}
    </div>
  );
}

/* ---------------- POTONG & BUNDEL ---------------- */
function StageCutting({ spks, batches, reload, canCut }) {
  const eligible = spks.filter((s) => ["diserahkan", "proses_potong"].includes(s.status) && !s.hold_pending && (s.variants || []).some((v) => !(s.generated_variants || []).includes(v.variant)));
  const [spkId, setSpkId] = useState("");
  const [variant, setVariant] = useState("");
  const [totalQty, setTotalQty] = useState("");
  const [perBundle, setPerBundle] = useState("");
  const [withRemainder, setWithRemainder] = useState(true);
  const [editRows, setEditRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [printBatch, setPrintBatch] = useState(null);
  const [f, setF] = useState(EMPTY_FILTER);
  const spk = spks.find((s) => s.id === spkId);

  const rows = useMemo(() => {
    if (editRows) return editRows;
    const t = parseInt(totalQty) || 0, p = parseInt(perBundle) || 0;
    if (t <= 0 || p <= 0) return [];
    const full = Math.floor(t / p);
    const rem = t % p;
    const arr = Array.from({ length: full }, () => p);
    if (rem > 0 && withRemainder) arr.push(rem);
    return arr;
  }, [totalQty, perBundle, withRemainder, editRows]);

  const allocated = rows.reduce((a, b) => a + b, 0);
  const t = parseInt(totalQty) || 0;
  const balance = t - allocated;
  const rem = t > 0 && (parseInt(perBundle) || 0) > 0 ? t % (parseInt(perBundle) || 1) : 0;

  const save = async (generate) => {
    if (!spkId) return toast.error("Pilih SPK Potong");
    if (!variant) return toast.error("Pilih variasi dari SPK");
    if (!(t > 0)) return toast.error("Isi total hasil potong");
    if (!rows.length) return toast.error("Pembagian bundel kosong");
    setBusy(true);
    try {
      const r = await api.post("/prod/batches", { spk_id: spkId, variant, total_qty: t, bundle_qtys: rows, generate });
      toast.success(generate ? `Batch ${r.data.number} digenerate — ${r.data.bundles.length} bundel Ready Jahit` : "Draft pembagian tersimpan");
      if (generate) { setSpkId(""); setVariant(""); setTotalQty(""); setPerBundle(""); setEditRows(null); setShowForm(false); setPrintBatch(r.data); }
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };

  const fb = batches.filter((b) => b.status !== "draft" && matchFilter(b, f, b.generated_at));
  const blAll = fb.flatMap((b) => b.bundles || []);
  const roots = blAll.filter((x) => !x.parent_id);
  const assigned = roots.filter((x) => x.sewer_id);
  const totalPotong = fb.reduce((a, b) => a + (b.total_qty || 0), 0);
  const totalBundel = fb.reduce((a, b) => a + (b.bundle_count || 0), 0);
  const dibagikan = assigned.reduce((a, x) => a + x.qty, 0);
  const setorPcs = assigned.reduce((a, x) => a + (x.qty - (x.ready_qty || 0)), 0);
  const readyRoots = roots.filter((x) => x.status === "ready_jahit" && !x.sewer_id);
  const sisaReady = readyRoots.reduce((a, x) => a + (x.ready_qty || 0), 0);
  const { paged: pagedBatches, pager: batchPager } = usePager(fb, 10);

  return (
    <div className="space-y-4" data-testid="stage-cutting">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Hasil Potong & Bundel</h2>
          <p className="text-xs text-slate-400">Pantau total hasil potong dan pembagian bundel ke setiap penjahit.</p>
        </div>
        {canCut && <button data-testid="cut-open-form" onClick={() => setShowForm(true)} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Input Hasil Potong</button>}
      </div>
      <StageFilters items={batches.filter((b) => b.status !== "draft")} value={f} onChange={setF} testid="cut" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <StageStat icon={Layers} tone="rose" label="Total Hasil Potong" value={`${totalPotong.toLocaleString("id-ID")} pcs`} sub={`${totalBundel} bundel`} />
        <StageStat icon={Truck} tone="sky" label="Sudah ke Penjahit" value={`${dibagikan.toLocaleString("id-ID")} pcs`} sub={`${assigned.length} bundel • ${new Set(assigned.map((x) => x.sewer_id)).size} penjahit`} />
        <StageStat icon={Package} tone="amber" label="Sisa Ready" value={`${sisaReady.toLocaleString("id-ID")} pcs`} sub={`${readyRoots.length} bundel belum dibagikan`} />
        <StageStat icon={Check} tone="emerald" label="Sudah Setor Jahit" value={`${setorPcs.toLocaleString("id-ID")} pcs`} sub={`Sisa di penjahit ${(dibagikan - setorPcs).toLocaleString("id-ID")} pcs`} />
      </div>
      <SewerShareTable bundles={blAll} testid="cut" />
      {showForm && (
      <Modal tid="cut-form-modal" title="Input Hasil Potong" onClose={() => setShowForm(false)} xl>
      <div className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">SPK Potong</label>
            <select data-testid="cut-spk" value={spkId} onChange={(e) => { setSpkId(e.target.value); setVariant(""); setEditRows(null); }} className={inpL} disabled={!canCut}>
              <option value="">Pilih SPK</option>
              {eligible.map((s) => <option key={s.id} value={s.id}>{`${s.number} — target ${s.target_qty.toLocaleString("id-ID")}`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Tanggal Produksi</label>
            <input data-testid="cut-date" type="date" defaultValue={new Date().toISOString().slice(0, 10)} className={inpL} readOnly />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Variasi (dari SPK) <span className="text-red-500">*</span></label>
            <select data-testid="cut-variant" value={variant} onChange={(e) => { setVariant(e.target.value); setEditRows(null); }} className={inpL} disabled={!spkId || !canCut}>
              <option value="">Pilih variasi</option>
              {((spk?.variants || []).filter((v) => !(spk?.generated_variants || []).includes(v.variant))).map((v) => <option key={v.variant} value={v.variant}>{`${v.variant} — target ${v.target_qty.toLocaleString("id-ID")}`}</option>)}
            </select>
            {spk && <p className="text-[10px] text-slate-400 mt-1">{spk.product_name} • batch & bundel dibuat terpisah per variasi</p>}
          </div>
        </div>
        {spk && variant && t > 0 && t !== ((spk.variants || []).find((v) => v.variant === variant)?.target_qty) && (
          <div data-testid="cut-diff" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5">Hasil aktual ({t.toLocaleString("id-ID")}) berbeda dari target variasi ({(((spk.variants || []).find((v) => v.variant === variant)?.target_qty) || 0).toLocaleString("id-ID")}) — ditampilkan apa adanya, tidak disamakan otomatis.</div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Total Hasil Potong</label>
            <div className="relative"><input data-testid="cut-total" type="number" min="1" value={totalQty} onChange={(e) => { setTotalQty(e.target.value); setEditRows(null); }} className={`${inpL} pr-12`} disabled={!canCut} /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">pcs</span></div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Qty per Bundel</label>
            <div className="relative"><input data-testid="cut-per" type="number" min="1" value={perBundle} onChange={(e) => { setPerBundle(e.target.value); setEditRows(null); }} className={`${inpL} pr-12`} disabled={!canCut} /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">pcs</span></div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wide text-slate-400">Total Bundel</div><div data-testid="cut-total-bundles" className="font-display text-lg font-bold text-slate-900">{rows.length}</div></div>
          <div className="bg-slate-50 rounded-xl p-3 text-center"><div className="text-[10px] uppercase tracking-wide text-slate-400">Qty Teralokasi</div><div data-testid="cut-allocated" className="font-display text-lg font-bold text-slate-900">{allocated.toLocaleString("id-ID")} pcs</div></div>
          <div className={`rounded-xl p-3 text-center ${balance === 0 && t > 0 ? "bg-emerald-50" : "bg-red-50"}`}><div className="text-[10px] uppercase tracking-wide text-slate-400">Balance</div><div data-testid="cut-balance" className={`font-display text-lg font-bold ${balance === 0 && t > 0 ? "text-emerald-600" : "text-red-500"}`}>{balance.toLocaleString("id-ID")} pcs</div></div>
        </div>
        {rem > 0 && !editRows && (
          <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="cut-remainder" checked={withRemainder} onChange={(e) => setWithRemainder(e.target.checked)} className="w-4 h-4 accent-[#7F1D2D]" />
            Tidak habis dibagi — tambahkan bundel terakhir berisi sisa {rem} pcs
          </label>
        )}
        {rows.length > 0 && (
          <div data-testid="cut-preview">
            <div className="flex items-center gap-2 mb-2"><span className="font-display font-bold text-sm text-slate-900">Pratinjau Pembagian</span><span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">Belum Digenerate</span></div>
            <div className="border border-slate-100 rounded-xl overflow-hidden max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50/60 text-left text-[10px] uppercase text-slate-400"><th className="px-4 py-2">No. Bundel</th><th className="px-4 py-2">Qty per Bundel</th></tr></thead>
                <tbody>
                  {rows.map((q, i) => (
                    <tr key={i} className="border-t border-slate-50">
                      <td className="px-4 py-1.5 font-mono text-slate-500">{String(i + 1).padStart(3, "0")}</td>
                      <td className="px-4 py-1.5">
                        {canCut ? <input data-testid={`cut-row-${i}`} type="number" min="1" value={q} onChange={(e) => { const v = parseInt(e.target.value) || 0; setEditRows(rows.map((x, j) => (j === i ? v : x))); }} className="h-8 w-24 px-2 rounded-lg border border-slate-200 text-xs" /> : `${q} pcs`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">{rows.length} bundel • Total {allocated.toLocaleString("id-ID")} pcs • Nomor batch dan bundel dibuat otomatis setelah disimpan.</p>
          </div>
        )}
        {canCut && (
          <div className="flex gap-2 justify-end">
            <button data-testid="cut-draft" onClick={() => save(false)} disabled={busy || !rows.length} className="h-10 px-5 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-xs font-semibold disabled:opacity-40">Simpan Draft</button>
            <button data-testid="cut-generate" onClick={() => save(true)} disabled={busy || balance !== 0 || !rows.length} className="h-10 px-5 rounded-xl text-white text-xs font-semibold disabled:opacity-40" style={{ background: MAROON }}>Generate Batch & Bundel</button>
          </div>
        )}
      </div>
      </Modal>
      )}
      {printBatch && <BatchPrintModal batch={printBatch} onClose={() => setPrintBatch(null)} />}

      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display font-bold text-sm text-slate-900">Rincian Batch & Bundel</h3>
          <span className="text-[10px] text-slate-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Batch terhubung ke produk, variasi, dan penjahit.</span>
        </div>
        {pagedBatches.map((b) => <BatchRincianCard key={b.id} b={b} />)}
        {!fb.length && <div className={`${card} py-8 text-center text-xs text-slate-400`} data-testid="cut-batch-empty">Belum ada batch yang cocok dengan filter.</div>}
        <Pager {...batchPager} testid="cut-pager" />
      </div>
    </div>
  );
}

/* ---------------- JAHIT ---------------- */
function AssignModal({ bundles, employees, onClose, onSaved }) {
  const [sewerQ, setSewerQ] = useState("");
  const [sewerId, setSewerId] = useState("");
  const [bundleId, setBundleId] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = bundles.filter((b) => b.status === "ready_jahit" && !b.parent_id && !b.sewer_id && b.ready_qty > 0);
  const bundle = bundles.find((b) => b.id === bundleId);
  const [bq, setBq] = useState("");
  const bqLower = bq.trim().toLowerCase();
  const bOpts = bqLower ? ready.filter((b) => `${b.product_name} ${b.variant} ${b.number} ${b.spk_number || ""}`.toLowerCase().includes(bqLower)) : [];
  const sewer = employees.find((e) => e.id === sewerId);
  const pool = employees.filter((e) => e.role === "penjahit");
  const sewerOpts = pool.filter((e) => (e.name + (e.employee_id || "")).toLowerCase().includes(sewerQ.toLowerCase()));

  const submit = async () => {
    if (!sewerId) return toast.error("Pilih penjahit");
    if (!bundleId) return toast.error("Pilih bundel Ready");
    setBusy(true);
    try {
      const r = await api.post(`/prod/bundles/${bundleId}/assign`, { sewer_id: sewerId });
      toast.success(`Bundel ${r.data.number} dibagikan ke ${r.data.sewer_name} — status Di Penjahit`);
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membagikan bundel"); } finally { setBusy(false); }
  };

  return (
    <Modal tid="sew-assign-modal" title="Bagikan Bundel ke Penjahit" onClose={onClose}>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold text-slate-500">ID / Nama Penjahit</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="sewa-sewer-q" value={sewerQ} onChange={(e) => { setSewerQ(e.target.value); setSewerId(""); }} placeholder="Cari nama atau ID karyawan" className={`${inpL} pl-9`} />
          </div>
          {sewerQ && !sewerId && (
            <div className="mt-1 border border-slate-200 rounded-xl overflow-hidden max-h-40 overflow-y-auto bg-white">
              {sewerOpts.slice(0, 8).map((e) => (
                <button key={e.id} data-testid={`sewa-sewer-${e.id}`} onClick={() => { setSewerId(e.id); setSewerQ(`${e.name} (${e.employee_id || e.role_label})`); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-rose-50/40 text-slate-700">{e.name} • <span className="text-slate-400">{e.employee_id || e.role_label}</span></button>
              ))}
              {!sewerOpts.length && <p className="px-3 py-2 text-[10px] text-slate-400">Tidak ada karyawan cocok.</p>}
            </div>
          )}
          {!pool.length && <p className="text-[10px] text-amber-600 mt-1">Belum ada karyawan role Penjahit — atur role di menu Karyawan.</p>}
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Bundel Ready (belum dibagikan)</label>
          {bundle ? (
            <div data-testid="sewa-bsel" className="flex items-center justify-between gap-2 h-10 px-3 rounded-xl border border-emerald-200 bg-emerald-50/50">
              <span className="text-xs font-semibold text-slate-700 truncate"><span className="font-mono">{bundle.number}</span> — {bundle.product_name} {bundle.variant} • {bundle.ready_qty} pcs</span>
              <button type="button" data-testid="sewa-bclear" onClick={() => { setBundleId(""); setBq(""); }} className="text-slate-400 hover:text-slate-600 shrink-0"><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="sewa-bq" value={bq} onChange={(e) => setBq(e.target.value)} placeholder="Ketik nama produk / variasi / kode bundel" className={`${inpL} pl-9`} />
              </div>
              {bq.trim() && (
                <div data-testid="sewa-blist" className="mt-1 border border-slate-200 rounded-xl overflow-hidden max-h-44 overflow-y-auto bg-white">
                  {bOpts.slice(0, 20).map((b) => (
                    <button key={b.id} data-testid={`sewa-b-${b.id}`} onClick={() => setBundleId(b.id)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-rose-50/40 border-b border-slate-50 last:border-0">
                      <span className="font-mono font-semibold text-slate-700">{b.number}</span>
                      <span className="text-slate-500"> — {b.product_name} {b.variant} • {b.ready_qty} pcs</span>
                    </button>
                  ))}
                  {!bOpts.length && <p className="px-3 py-2 text-[10px] text-slate-400">Tidak ada bundel ready cocok.</p>}
                </div>
              )}
            </>
          )}
          {!ready.length && <p className="text-[10px] text-slate-400 mt-1">Tidak ada bundel ready — generate dulu di Potong & Bundel.</p>}
        </div>
      </div>
      {bundle && sewer && (
        <div data-testid="sewa-info" className="text-[11px] bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-wrap gap-x-6 gap-y-1">
          <span>Bundel: <b className="font-mono">{bundle.number}</b></span>
          <span>Produk: <b>{bundle.product_name} • {bundle.variant}</b></span>
          <span>Penjahit: <b>{sewer.name}</b></span>
          <span>Isi: <b>{bundle.ready_qty} pcs</b></span>
        </div>
      )}
      <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg p-2">Bundel berstatus Di Penjahit setelah dibagikan. Hasil selesai dicatat lewat tombol Input Selesai Jahit.</p>
      <button data-testid="sewa-submit" onClick={submit} disabled={busy} className="w-full h-10 rounded-xl text-white text-xs font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Bagikan Bundel"}</button>
    </Modal>
  );
}

function SelesaiModal({ bundles, employees, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [bundleId, setBundleId] = useState("");
  const [qty, setQty] = useState("");
  const [freeQ, setFreeQ] = useState("");
  const [freeId, setFreeId] = useState("");
  const [freeSewerQ, setFreeSewerQ] = useState("");
  const [freeSewerId, setFreeSewerId] = useState("");
  const [busy, setBusy] = useState(false);
  const working = bundles.filter((b) => b.status === "ready_jahit" && !b.parent_id && b.sewer_id && b.ready_qty > 0);
  // Bundel ready yang belum dikerjakan sama sekali (belum dibagikan ke penjahit mana pun)
  const free = bundles.filter((b) => b.status === "ready_jahit" && !b.parent_id && !b.sewer_id && b.ready_qty === b.qty);
  const bundle = bundles.find((b) => b.id === bundleId);
  const freeBundle = bundles.find((b) => b.id === freeId);
  const target = bundle || freeBundle;
  const query = q.trim().toLowerCase();
  const [sewerSel, setSewerSel] = useState("");
  const selSewer = employees.find((e) => e.id === sewerSel);
  // Pencarian: yang tampil dulu NAMA KARYAWAN-nya; bundel baru muncul setelah karyawan dipilih.
  // Kode bundel / no SPK tetap bisa dicari langsung bila tidak ada karyawan yang cocok.
  const empMatches = query ? employees.filter((e) => (e.name + (e.employee_id || "")).toLowerCase().includes(query)) : [];
  const directBundles = query ? working.filter((b) => b.number.toLowerCase().includes(query) || (b.spk_number || "").toLowerCase().includes(query)) : [];
  const list = sewerSel ? working.filter((b) => b.sewer_id === sewerSel) : [];
  const fq = freeQ.trim().toLowerCase();
  const freeList = fq
    ? free.filter((b) => (b.product_name || "").toLowerCase().includes(fq) || (b.variant || "").toLowerCase().includes(fq) || b.number.toLowerCase().includes(fq) || (b.spk_number || "").toLowerCase().includes(fq))
    : [];
  const fsq = freeSewerQ.trim().toLowerCase();
  const freeSewerOpts = fsq ? employees.filter((e) => (e.name + (e.employee_id || "")).toLowerCase().includes(fsq)) : [];
  const freeSewer = employees.find((e) => e.id === freeSewerId);
  const maxQty = bundle ? (bundle.ready_qty || 0) : (freeBundle?.qty || 0);
  const qtyNum = parseInt(qty) || 0;
  const qtyOk = qtyNum > 0 && qtyNum <= maxQty;

  const pick = (b) => { setBundleId(b.id); setFreeId(""); setFreeSewerId(""); setFreeSewerQ(""); setQty(String(b.ready_qty)); };
  const pickFree = (b) => { setFreeId(b.id); setBundleId(""); setQty(String(b.qty)); };

  const submit = async () => {
    if (!target) return toast.error("Pilih bundel yang sedang dikerjakan atau bundel ready");
    if (freeBundle && !freeSewerId) return toast.error("Pilih penjahit yang menyetor bundel ini");
    if (!qtyOk) return toast.error(`Qty selesai wajib 1 sampai ${maxQty} pcs`);
    setBusy(true);
    try {
      const r = await api.post("/prod/sewing/submit", { sewer_id: targetSewerId(), bundle_id: target.id, qty: qtyNum });
      toast.success(`Selesai jahit tercatat — ${r.data.bundle.number} masuk antrean QC`);
      setBundleId(""); setFreeId(""); setFreeSewerId(""); setFreeSewerQ(""); setQty("");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat selesai jahit"); } finally { setBusy(false); }
  };
  const targetSewerId = () => (bundle ? bundle.sewer_id : freeSewerId);

  return (
    <Modal tid="sew-done-modal" title="Input Selesai Jahit" onClose={onClose}>
      <BundleScanInput testid="sewd-scan" onFound={(code) => {
        const b = working.find((x) => x.number === code) || free.find((x) => x.number === code);
        if (!b) { toast.error(`Bundel ${code} tidak ditemukan atau sedang diproses`); return; }
        if (b.sewer_id) { pick(b); } else { pickFree(b); }
        toast.success(`Bundel ${b.number} terpilih dari scan`);
      }} />
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="sewd-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / ID karyawan, kode bundel, atau no SPK" className={`${inpL} pl-9`} />
      </div>
      {selSewer && (
        <div className="flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5" data-testid="sewd-sewer-chip">
          <span>Penjahit: <b>{selSewer.name}</b> ({selSewer.employee_id || "-"})</span>
          <button data-testid="sewd-sewer-clear" onClick={() => setSewerSel("")} className="ml-auto text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <div data-testid="sewd-list" className="border border-slate-200 rounded-xl max-h-56 overflow-y-auto divide-y divide-slate-50">
        {!selSewer && empMatches.map((e) => {
          const bl = working.filter((b) => b.sewer_id === e.id);
          const sisa = bl.reduce((a, b) => a + b.ready_qty, 0);
          return (
            <button key={e.id} data-testid={`sewd-sewer-${e.id}`} onClick={() => setSewerSel(e.id)} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between gap-2">
              <div>
                <div className="text-xs font-semibold text-slate-800">{e.name}</div>
                <div className="text-[10px] text-slate-400">ID: {e.employee_id || "-"}</div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold whitespace-nowrap">{bl.length} bundel • sisa {sisa} pcs</span>
            </button>
          );
        })}
        {(selSewer ? list : empMatches.length ? [] : directBundles).map((b) => (
          <button key={b.id} data-testid={`sewd-pick-${b.id}`} onClick={() => pick(b)}
            className={`w-full text-left px-3 py-2.5 transition-colors ${bundleId === b.id ? "bg-[#FBF3F4] border-l-2 border-[#7F1D2D]" : "hover:bg-slate-50"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] font-bold text-slate-700">{b.number}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">Sisa {b.ready_qty} pcs</span>
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">{b.product_name} • {b.variant}{b.spk_number ? ` • ${b.spk_number}` : ""}</div>
            <div className="text-[10px] text-slate-400">Penjahit: {b.sewer_name}</div>
          </button>
        ))}
        {!query && !selSewer && <p className="px-3 py-6 text-center text-[11px] text-slate-400">Ketik nama / ID karyawan untuk memilih penjahit, atau langsung ketik kode bundel / no SPK.</p>}
        {query && !selSewer && !empMatches.length && !directBundles.length && <p className="px-3 py-6 text-center text-[11px] text-slate-400">Tidak ada karyawan atau bundel cocok dengan pencarian.</p>}
        {selSewer && !list.length && <p className="px-3 py-6 text-center text-[11px] text-slate-400">{selSewer.name} tidak sedang mengerjakan bundel apa pun.</p>}
      </div>
      {bundle && (
        <div data-testid="sewd-info" className="text-[11px] bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-wrap gap-x-6 gap-y-1">
          <span>Bundel: <b className="font-mono">{bundle.number}</b></span>
          <span>Produk: <b>{bundle.product_name} • {bundle.variant}</b></span>
          <span>Penjahit: <b>{bundle.sewer_name}</b></span>
          <span>Sisa dikerjakan: <b>{bundle.ready_qty} pcs</b></span>
        </div>
      )}
      <div className="border-t border-dashed border-slate-200 pt-3 space-y-2">
        <div className="text-[11px] font-semibold text-slate-500">Penjahit lupa dibagikan? Cari bundel ready yang belum dikerjakan siapa pun</div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="sewd-free-q" value={freeQ} onChange={(e) => setFreeQ(e.target.value)} placeholder="Cari nama produk, variasi, atau no bundel (status ready)" className={`${inpL} pl-9`} />
        </div>
        {fq.length > 0 && (
          <div data-testid="sewd-free-list" className="border border-slate-200 rounded-xl max-h-44 overflow-y-auto divide-y divide-slate-50">
            {freeList.map((b) => (
              <button key={b.id} data-testid={`sewd-free-pick-${b.id}`} onClick={() => pickFree(b)}
                className={`w-full text-left px-3 py-2.5 transition-colors ${freeId === b.id ? "bg-[#FBF3F4] border-l-2 border-[#7F1D2D]" : "hover:bg-slate-50"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] font-bold text-slate-700">{b.number}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 font-semibold">Ready • {b.qty} pcs</span>
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">{b.product_name} • {b.variant}{b.spk_number ? ` • ${b.spk_number}` : ""}</div>
              </button>
            ))}
            {!freeList.length && <p className="px-3 py-4 text-center text-[11px] text-slate-400">Tidak ada bundel ready yang cocok.</p>}
          </div>
        )}
        {freeBundle && (
          <div className="space-y-2">
            <div data-testid="sewd-free-info" className="text-[11px] bg-sky-50 border border-sky-100 rounded-xl p-3 flex flex-wrap gap-x-6 gap-y-1">
              <span>Bundel: <b className="font-mono">{freeBundle.number}</b></span>
              <span>Produk: <b>{freeBundle.product_name} • {freeBundle.variant}</b></span>
              <span>Status: <b>Ready — belum dikerjakan siapa pun</b></span>
            </div>
            {freeSewer ? (
              <div className="flex items-center gap-2 text-[11px] bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                <span>Menyetor: <b>{freeSewer.name}</b> ({freeSewer.employee_id || "-"})</span>
                <button data-testid="sewd-free-sewer-clear" onClick={() => { setFreeSewerId(""); setFreeSewerQ(""); }} className="ml-auto text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="sewd-free-sewer-q" value={freeSewerQ} onChange={(e) => setFreeSewerQ(e.target.value)} placeholder="Pilih penjahit yang menyetor — cari nama / ID" className={`${inpL} pl-9`} />
              </div>
            )}
            {!freeSewer && fsq.length > 0 && (
              <div data-testid="sewd-free-sewer-list" className="border border-slate-200 rounded-xl max-h-36 overflow-y-auto divide-y divide-slate-50">
                {freeSewerOpts.slice(0, 8).map((e) => (
                  <button key={e.id} data-testid={`sewd-free-sewer-${e.id}`} onClick={() => setFreeSewerId(e.id)} className="w-full text-left px-3 py-2 text-xs hover:bg-rose-50/40 text-slate-700">{e.name} • <span className="text-slate-400">{e.employee_id || e.role_label}</span></button>
                ))}
                {!freeSewerOpts.length && <p className="px-3 py-2 text-[10px] text-slate-400">Tidak ada karyawan cocok.</p>}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <div>
          <label className="text-[11px] font-semibold text-slate-500">Qty Selesai</label>
          <input data-testid="sewd-qty" type="number" min="1" max={maxQty || undefined} value={qty} onChange={(e) => setQty(e.target.value)} className={inpL} />
          {target && qtyOk && qtyNum < maxQty && <p className="text-[10px] text-slate-400 mt-1">Sebagian — sisa {maxQty - qtyNum} pcs {bundle ? "tetap di penjahit dan bundel tetap muncul di list." : "tetap ready (belum dibagikan)."}</p>}
        </div>
        <button data-testid="sewd-submit" onClick={submit} disabled={busy || !target || !qtyOk || (freeBundle && !freeSewerId)} className="h-10 rounded-xl text-white text-xs font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Selesaikan & Kirim ke QC"}</button>
      </div>
    </Modal>
  );
}

function StageSewing({ bundles, employees, reload, canSew }) {
  const [showAssign, setShowAssign] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [f, setF] = useState(EMPTY_FILTER);
  const fb = bundles.filter((b) => matchFilter(b, f, b.created_at));
  const roots = fb.filter((x) => !x.parent_id);
  const assigned = roots.filter((x) => x.sewer_id);
  const dibagikan = assigned.reduce((a, x) => a + x.qty, 0);
  const setorPcs = assigned.reduce((a, x) => a + (x.qty - (x.ready_qty || 0)), 0);
  const readyRoots = roots.filter((x) => x.status === "ready_jahit" && !x.sewer_id && (x.ready_qty || 0) > 0);
  const sisaReady = readyRoots.reduce((a, x) => a + (x.ready_qty || 0), 0);
  const inQc = fb.filter((x) => x.parent_id).reduce((a, x) => a + x.qty, 0);
  const { paged: pagedBundles, pager: bundlePager } = usePager(fb, 10);

  return (
    <div className="space-y-4" data-testid="stage-sewing">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Setoran Jahit</h2>
          <p className="text-xs text-slate-400">Penjahit tidak wajib input/scan QR — mandor mencatat setelah hasil diserahkan. Saldo berkurang atomik, anti setoran ganda.</p>
        </div>
        {canSew && (
          <div className="flex gap-2">
            <button data-testid="sew-assign-btn" onClick={() => setShowAssign(true)} className="h-10 px-4 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-sm font-semibold flex items-center gap-1.5"><Truck className="w-4 h-4" /> Bagikan Bundel</button>
            <button data-testid="sew-done-btn" onClick={() => setShowDone(true)} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5" style={{ background: MAROON }}><Check className="w-4 h-4" /> Input Selesai Jahit</button>
          </div>
        )}
      </div>
      <StageFilters items={bundles} value={f} onChange={setF} testid="sew" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <StageStat icon={Package} tone="amber" label="Siap Dibagikan" value={`${sisaReady.toLocaleString("id-ID")} pcs`} sub={`${readyRoots.length} bundel menunggu`} />
        <StageStat icon={Truck} tone="sky" label="Di Penjahit" value={`${(dibagikan - setorPcs).toLocaleString("id-ID")} pcs`} sub={`${assigned.length} bundel • ${new Set(assigned.map((x) => x.sewer_id)).size} penjahit`} />
        <StageStat icon={Check} tone="emerald" label="Sudah Setor" value={`${setorPcs.toLocaleString("id-ID")} pcs`} sub="Sudah diserahkan mandor" />
        <StageStat icon={ClipboardCheck} tone="rose" label="Masuk QC" value={`${inQc.toLocaleString("id-ID")} pcs`} sub="Menunggu / dalam pemeriksaan" />
      </div>
      <SewerShareTable bundles={fb} testid="sew" />
      <div className={`${card} overflow-hidden`}>
        <div className="px-4 pt-4 pb-2 font-display font-bold text-slate-900 text-sm">Saldo Bundel (Belum Tercatat Setoran)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100"><th className="px-4 py-2">Bundel</th><th className="px-3 py-2">Produk</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Saldo Ready</th><th className="px-3 py-2">Penjahit</th><th className="px-4 py-2">Status</th></tr></thead>
            <tbody>
              {pagedBundles.map((b) => {
                const [lb, cls] = BUNDLE_STATUS[b.status] || [b.status, "bg-slate-100 text-slate-500"];
                return (
                  <tr key={b.id} data-testid={`sew-row-${b.id}`} className="border-b border-slate-50">
                    <td className="px-4 py-2.5 font-mono text-slate-600">{b.number}{b.parent_id && <span className="text-slate-300"> ↳ bagian</span>}</td>
                    <td className="px-3 py-2.5 text-slate-600">{b.product_name} • {b.variant}</td>
                    <td className="px-3 py-2.5">{b.qty}</td>
                    <td className="px-3 py-2.5">{b.ready_qty > 0 ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">{b.ready_qty} • Belum Tercatat Setoran</span> : "0"}</td>
                    <td className="px-3 py-2.5 text-slate-600">{b.sewer_name || "—"}</td>
                    <td className="px-4 py-2.5">{b.status === "ready_jahit" && b.sewer_id
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-600">Di Penjahit</span>
                      : <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{lb}</span>}</td>
                  </tr>
                );
              })}
              {!fb.length && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Tidak ada bundel yang cocok dengan filter — generate dulu di tahap Potong & Bundel.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <Pager {...bundlePager} testid="sew-pager" />
      {showAssign && <AssignModal bundles={bundles} employees={employees} onClose={() => setShowAssign(false)} onSaved={reload} />}
      {showDone && <SelesaiModal bundles={bundles} employees={employees} onClose={() => setShowDone(false)} onSaved={reload} />}
    </div>
  );
}

/* ---------------- FINISHING & QC ---------------- */
function QcModal({ bundles, employees, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [bundleId, setBundleId] = useState("");
  const [form, setForm] = useState({ physical: "", passed: "", repair: "", reject: "", repair_reason: "", reject_reason: "", reject_photo: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const p = (k) => parseInt(form[k]) || 0;
  const sum = p("passed") + p("repair") + p("reject");
  const valid = sum === p("physical") && p("physical") >= 0;
  const pending = bundles.filter((b) => b.status === "dijahit" && b.sewer_id);
  const bundle = bundles.find((b) => b.id === bundleId);
  const query = q.trim().toLowerCase();
  const empIds = new Set(employees.filter((e) => (e.name + (e.employee_id || "")).toLowerCase().includes(query)).map((e) => e.id));
  const list = (query
    ? pending.filter((b) => empIds.has(b.sewer_id) || b.number.toLowerCase().includes(query) || (b.spk_number || "").toLowerCase().includes(query) || (b.product_name || "").toLowerCase().includes(query))
    : []
  ).sort((a, b) => (a.sewer_name || "").localeCompare(b.sewer_name || ""));

  const pick = (b) => { setBundleId(b.id); setForm({ physical: String(b.qty), passed: "", repair: "", reject: "", repair_reason: "", reject_reason: "", reject_photo: "" }); };
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/uploads", fd); setForm((s) => ({ ...s, reject_photo: data.url })); toast.success("Foto terunggah"); }
    catch { toast.error("Gagal mengunggah foto"); }
  };
  const submit = async () => {
    if (!bundle) return toast.error("Pilih bundel yang akan diperiksa");
    if (!valid) return toast.error("Lolos + perbaikan + reject harus sama dengan jumlah fisik");
    setBusy(true);
    try {
      await api.post("/prod/qc/inspect", {
        bundle_id: bundle.id, physical_qty: p("physical"), passed: p("passed"), repair: p("repair"), reject: p("reject"),
        repair_destination: p("repair") > 0 ? `Kembali ke penjahit: ${bundle.sewer_name}` : "",
        repair_reason: form.repair_reason,
        reject_reason: form.reject_reason, reject_photo: form.reject_photo || null,
      });
      toast.success(`Pemeriksaan ${bundle.number} tersimpan — lolos ${p("passed")}, perbaikan ${p("repair")}, reject ${p("reject")}`);
      setBundleId(""); setQ("");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan pemeriksaan"); } finally { setBusy(false); }
  };

  return (
    <Modal tid="qcf-modal" title="Input Hasil QC" onClose={onClose} wide>
      <BundleScanInput testid="qc-scan" onFound={(code) => {
        const b = pending.find((x) => x.number === code);
        if (!b) { toast.error(`Bundel ${code} tidak ada di antrean QC`); return; }
        pick(b); toast.success(`Bundel ${b.number} terpilih dari scan`);
      }} />
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="qc-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / ID penjahit, kode bundel, no SPK, atau nama produk" className={`${inpL} pl-9`} />
      </div>
      <div data-testid="qc-list" className="border border-slate-200 rounded-xl max-h-52 overflow-y-auto divide-y divide-slate-50">
        {list.map((b) => (
          <button key={b.id} data-testid={`qc-pick-${b.id}`} onClick={() => pick(b)}
            className={`w-full text-left px-3 py-2.5 transition-colors ${bundleId === b.id ? "bg-[#FBF3F4] border-l-2 border-[#7F1D2D]" : "hover:bg-slate-50"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] font-bold text-slate-700">{b.number}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">Tercatat {b.qty} pcs</span>
            </div>
            <div className="text-[11px] text-slate-600 mt-0.5">{b.product_name} • {b.variant}{b.spk_number ? ` • ${b.spk_number}` : ""}</div>
            <div className="text-[10px] text-slate-400">Penjahit: {b.sewer_name}</div>
          </button>
        ))}
        {!list.length && <p className="px-3 py-6 text-center text-[11px] text-slate-400">{!query ? "Ketik nama / ID penjahit, kode bundel, atau nama produk untuk menampilkan antrean QC." : pending.length ? "Tidak ada bundel cocok dengan pencarian." : "Belum ada bundel masuk QC — catat setoran lewat Input Selesai Jahit."}</p>}
      </div>
      {bundle && (
        <div data-testid="qc-info" className="text-[11px] bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-wrap gap-x-6 gap-y-1">
          <span>Bundel: <b className="font-mono">{bundle.number}</b></span>
          <span>Produk: <b>{bundle.product_name} • {bundle.variant}</b></span>
          <span>Penjahit: <b>{bundle.sewer_name}</b></span>
          <span>Qty tercatat: <b>{bundle.qty} pcs</b></span>
        </div>
      )}
      {bundle && p("physical") < bundle.qty && <div data-testid="qcf-selisih-note" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5">Fisik kurang dari tercatat — pengajuan selisih {bundle.qty - p("physical")} pcs otomatis dibuat ke pengawas; hasil lolos tetap bisa dilanjutkan.</div>}
      {bundle && p("physical") > bundle.qty && <div data-testid="qcf-over-note" className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">Fisik melebihi tercatat — akan dibuatkan pengajuan selisih dan pemeriksaan ditahan sampai diputus pengawas.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div><label className="text-[11px] font-semibold text-slate-500">Jumlah Fisik</label><input data-testid="qcf-physical" type="number" min="0" value={form.physical} onChange={(e) => setForm({ ...form, physical: e.target.value })} className={inpL} disabled={!bundle} /></div>
        <div><label className="text-[11px] font-semibold text-emerald-600">Lolos</label><input data-testid="qcf-passed" type="number" min="0" value={form.passed} onChange={(e) => setForm({ ...form, passed: e.target.value })} className={inpL} disabled={!bundle} /></div>
        <div><label className="text-[11px] font-semibold text-amber-600">Perlu Perbaikan</label><input data-testid="qcf-repair" type="number" min="0" value={form.repair} onChange={(e) => setForm({ ...form, repair: e.target.value })} className={inpL} disabled={!bundle} /></div>
        <div><label className="text-[11px] font-semibold text-red-500">Reject Permanen</label><input data-testid="qcf-reject" type="number" min="0" value={form.reject} onChange={(e) => setForm({ ...form, reject: e.target.value })} className={inpL} disabled={!bundle} /></div>
      </div>
      <div data-testid="qcf-sum" className={`text-xs font-bold text-center rounded-xl py-2 ${bundle && valid ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
        {p("passed")} + {p("repair")} + {p("reject")} = {sum} {bundle && valid ? "= fisik ✓" : `≠ fisik ${p("physical")}`}
      </div>
      {bundle && p("repair") > 0 && (
        <div className="space-y-3">
          <div data-testid="qcf-repair-info" className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2.5">Perbaikan otomatis dikembalikan ke penjahit yang sama: <b>{bundle.sewer_name}</b>. Upah bagian ini ditahan sampai lolos periksa ulang.</div>
          <div><label className="text-[11px] font-semibold text-slate-500">Alasan perbaikan <span className="text-red-500">*</span></label><input data-testid="qcf-repair-reason" value={form.repair_reason} onChange={(e) => setForm({ ...form, repair_reason: e.target.value })} className={inpL} /></div>
        </div>
      )}
      {bundle && p("reject") > 0 && (
        <div className="space-y-3">
          <div className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">Reject permanen wajib alasan + foto, otomatis masuk Pengajuan Pengawas dan notifikasi.</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="text-[11px] font-semibold text-slate-500">Alasan reject <span className="text-red-500">*</span></label><input data-testid="qcf-reject-reason" value={form.reject_reason} onChange={(e) => setForm({ ...form, reject_reason: e.target.value })} className={inpL} /></div>
            <div><label className="text-[11px] font-semibold text-slate-500">Foto bukti <span className="text-red-500">*</span></label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
              <button data-testid="qcf-reject-photo" onClick={() => fileRef.current?.click()} className="w-full h-10 px-3 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500 flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> {form.reject_photo ? "Foto terlampir" : "Unggah foto"}</button>
            </div>
          </div>
        </div>
      )}
      <button data-testid="qcf-submit" onClick={submit} disabled={busy || !bundle || !valid} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Simpan Pemeriksaan"}</button>
    </Modal>
  );
}

function RecheckModal({ repair, onClose, onSaved }) {
  const [form, setForm] = useState({ passed: String(repair.qty), reject: "0", note: "", reject_reason: "", reject_photo: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const p = (k) => parseInt(form[k]) || 0;
  const valid = p("passed") + p("reject") === repair.qty;
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/uploads", fd); setForm((s) => ({ ...s, reject_photo: data.url })); toast.success("Foto terunggah"); }
    catch { toast.error("Gagal mengunggah foto"); }
  };
  const submit = async () => {
    if (!valid) return toast.error("Lolos + reject harus sama dengan qty perbaikan");
    setBusy(true);
    try {
      await api.post("/prod/qc/recheck", { repair_id: repair.id, passed: p("passed"), reject: p("reject"), note: form.note, reject_reason: form.reject_reason, reject_photo: form.reject_photo || null });
      toast.success("Pemeriksaan ulang tersimpan — upah tidak dihitung dua kali"); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  return (
    <Modal tid="recheck-modal" title={`Periksa Ulang • ${repair.bundle_number} (${repair.qty} pcs)`} onClose={onClose}>
      <div className="text-[11px] text-slate-500">ID bundel tetap sama. Upah qty perbaikan yang lolos dilepas (bukan dibuat baru); yang reject menunggu keputusan pengawas.</div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-[11px] font-semibold text-emerald-600">Lolos</label><input data-testid="recheck-passed" type="number" min="0" value={form.passed} onChange={(e) => setForm({ ...form, passed: e.target.value })} className={inpL} /></div>
        <div><label className="text-[11px] font-semibold text-red-500">Reject</label><input data-testid="recheck-reject" type="number" min="0" value={form.reject} onChange={(e) => setForm({ ...form, reject: e.target.value })} className={inpL} /></div>
      </div>
      <div><label className="text-[11px] font-semibold text-slate-500">Catatan</label><input data-testid="recheck-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inpL} /></div>
      {p("reject") > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] font-semibold text-slate-500">Alasan reject <span className="text-red-500">*</span></label><input data-testid="recheck-reject-reason" value={form.reject_reason} onChange={(e) => setForm({ ...form, reject_reason: e.target.value })} className={inpL} /></div>
          <div><label className="text-[11px] font-semibold text-slate-500">Foto <span className="text-red-500">*</span></label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
            <button data-testid="recheck-photo" onClick={() => fileRef.current?.click()} className="w-full h-10 px-3 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500 flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> {form.reject_photo ? "Foto terlampir" : "Unggah foto"}</button>
          </div>
        </div>
      )}
      <button data-testid="recheck-submit" onClick={submit} disabled={busy || !valid} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Menyimpan..." : "Simpan Hasil Periksa Ulang"}</button>
    </Modal>
  );
}

function StageQc({ bundles, repairs, employees, reload, canQc }) {
  const [showInspect, setShowInspect] = useState(false);
  const [recheckFor, setRecheckFor] = useState(null);
  const [f, setF] = useState(EMPTY_FILTER);
  const fb = bundles.filter((b) => matchFilter(b, f, b.created_at));
  const pending = fb.filter((b) => b.status === "dijahit" && b.sewer_id);
  const openRepairs = repairs.filter((r) => ["proses", "disetor"].includes(r.status));
  const waitPcs = pending.reduce((a, b) => a + b.qty, 0);
  const checked = fb.reduce((a, b) => a + (b.qc?.physical_qty || 0), 0);
  const lolos = fb.reduce((a, b) => a + (b.unreceived || 0), 0);
  const repPcs = openRepairs.reduce((a, r) => a + r.qty, 0);
  return (
    <div className="space-y-4" data-testid="stage-qc">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Finishing & QC</h2>
          <p className="text-xs text-slate-400">Periksa bundel: lolos + perbaikan + reject = jumlah fisik. Selisih otomatis diajukan ke pengawas.</p>
        </div>
        {canQc && <button data-testid="qc-inspect-btn" onClick={() => setShowInspect(true)} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5" style={{ background: MAROON }}><ClipboardCheck className="w-4 h-4" /> Input Hasil QC</button>}
      </div>
      <StageFilters items={bundles} value={f} onChange={setF} testid="qc" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <StageStat icon={ClipboardCheck} tone="amber" label="Menunggu QC" value={`${waitPcs.toLocaleString("id-ID")} pcs`} sub={`${pending.length} bundel`} />
        <StageStat icon={CheckSquare} tone="sky" label="Sudah Diperiksa" value={`${checked.toLocaleString("id-ID")} pcs`} sub="Akumulasi fisik diperiksa" />
        <StageStat icon={Check} tone="emerald" label="Lolos QC" value={`${lolos.toLocaleString("id-ID")} pcs`} sub="Siap diserahkan ke gudang" />
        <StageStat icon={AlertTriangle} tone="rose" label="Perbaikan Berjalan" value={`${repPcs.toLocaleString("id-ID")} pcs`} sub={`${openRepairs.length} paket perbaikan`} />
      </div>
      <div className={`${card} p-4 flex items-center justify-between gap-3 flex-wrap`} data-testid="qc-pending-summary">
        <div>
          <div className="font-display font-bold text-sm text-slate-900">Antrean Pemeriksaan</div>
          <div className="text-xs text-slate-500 mt-0.5">{pending.length} bundel • {waitPcs.toLocaleString("id-ID")} pcs menunggu diperiksa — klik "Input Hasil QC" lalu cari nama / ID penjahit, kode bundel, atau nama produk.</div>
        </div>
        {canQc && pending.length > 0 && <button data-testid="qc-inspect-btn-2" onClick={() => setShowInspect(true)} className="h-9 px-4 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-xs font-semibold">Periksa Sekarang</button>}
      </div>
      <div className="space-y-2">
        <h3 className="font-display font-bold text-sm text-slate-900">Perbaikan Berjalan</h3>
        {openRepairs.map((r) => (
          <div key={r.id} data-testid={`repair-${r.id}`} className={`${card} p-4 flex items-center justify-between gap-3 flex-wrap`}>
            <div>
              <div className="font-mono text-xs font-bold text-slate-700">{r.bundle_number} <span className="font-sans font-normal text-slate-400">(ID bundel sama)</span></div>
              <div className="text-xs text-slate-500 mt-0.5">{r.qty} pcs → {r.destination} • {r.reason}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">Riwayat: {r.history?.length || 0} pemeriksaan</div>
            </div>
            {canQc && <button data-testid={`repair-recheck-${r.id}`} onClick={() => setRecheckFor(r)} className="h-9 px-4 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-xs font-semibold">Periksa Ulang</button>}
          </div>
        ))}
        {!openRepairs.length && <div className={`${card} py-6 text-center text-xs text-slate-400`}>Tidak ada perbaikan berjalan.</div>}
      </div>
      {showInspect && <QcModal bundles={bundles} employees={employees} onClose={() => setShowInspect(false)} onSaved={reload} />}
      {recheckFor && <RecheckModal repair={recheckFor} onClose={() => setRecheckFor(null)} onSaved={reload} />}
    </div>
  );
}

/* ---------------- GUDANG ---------------- */
function StageWarehouse({ ready, receipts, batches, reload, canWarehouse }) {
  const [recvFor, setRecvFor] = useState(null);
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [trackId, setTrackId] = useState(null);
  const [track, setTrack] = useState(null);
  const openTrack = async (bid) => {
    if (trackId === bid) { setTrackId(null); return; }
    setTrackId(bid); setTrack(null);
    try { const r = await api.get(`/prod/tracking/${bid}`); setTrack(r.data); } catch { toast.error("Gagal memuat tracking"); }
  };
  const receive = async () => {
    if (!(parseInt(qty) > 0)) return toast.error("Qty wajib diisi");
    setBusy(true);
    try {
      await api.post("/prod/warehouse/receive", { bundle_id: recvFor.id, qty: parseInt(qty) });
      toast.success("Penerimaan tercatat — stok barang jadi bertambah tepat satu kali");
      setRecvFor(null); setQty(""); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menerima"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="stage-warehouse">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-900">Gudang — Penerimaan Barang Jadi</h2>
        <p className="text-xs text-slate-400">Hanya penerimaan di sini yang menambah stok barang jadi, tepat satu kali. Rantai SPK → batch → bundel → penjahit → QC → gudang terjaga.</p>
      </div>
      <div className="space-y-2">
        {ready.map((b) => (
          <div key={b.id} data-testid={`wh-row-${b.id}`} className={`${card} p-4 flex items-center justify-between gap-3 flex-wrap`}>
            <div>
              <span className="font-mono text-xs font-bold text-slate-700">{b.number}</span>
              <div className="text-xs text-slate-500 mt-0.5">{b.product_name} • {b.variant} • lolos QC belum diterima: <b className="text-emerald-600">{b.unreceived} pcs</b></div>
            </div>
            {canWarehouse && <button data-testid={`wh-receive-${b.id}`} onClick={() => { setRecvFor(b); setQty(String(b.unreceived)); }} className="h-9 px-4 rounded-xl text-white text-xs font-semibold" style={{ background: MAROON }}>Terima Barang</button>}
          </div>
        ))}
        {!ready.length && <div className={`${card} py-8 text-center text-xs text-slate-400`} data-testid="wh-empty">Tidak ada hasil lolos QC menunggu penerimaan.</div>}
      </div>
      <div className="space-y-2">
        <h3 className="font-display font-bold text-sm text-slate-900">Tracking Batch</h3>
        {batches.filter((b) => b.status !== "draft").map((b) => (
          <div key={b.id} className={`${card} overflow-hidden`}>
            <button data-testid={`track-${b.id}`} onClick={() => openTrack(b.id)} className="w-full p-4 flex items-center justify-between text-left">
              <span className="font-mono text-xs font-bold text-slate-700">{b.number} <span className="font-sans font-normal text-slate-400">• {b.product_name} {b.variant} • {b.total_qty?.toLocaleString("id-ID")} pcs</span></span>
              <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${b.status === "clear" ? "bg-emerald-50 text-emerald-600" : "bg-sky-50 text-sky-600"}`}>{b.status === "clear" ? "Clear — semua qty berpenyelesaian" : "Berjalan"}</span>
            </button>
            {trackId === b.id && (
              <div className="px-4 pb-4 border-t border-slate-100" data-testid={`track-panel-${b.id}`}>
                {!track ? <p className="text-xs text-slate-400 py-3">Memuat...</p> : (
                  <div className="py-3 space-y-1.5 max-h-64 overflow-y-auto">
                    {track.events.map((ev) => (
                      <div key={ev.id} className="flex gap-2 text-[11px]">
                        <span className="text-slate-400 whitespace-nowrap">{formatDateID(ev.created_at)}</span>
                        <span className="text-slate-700"><b>{ev.action}</b> — {ev.detail} <span className="text-slate-400">({ev.user_name})</span></span>
                      </div>
                    ))}
                    {!track.events.length && <p className="text-xs text-slate-400">Belum ada peristiwa.</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={`${card} overflow-hidden`}>
        <div className="px-5 pt-4 pb-2 font-display font-bold text-slate-900 text-sm">Riwayat Penerimaan</div>
        {receipts.slice(0, 10).map((r) => (
          <div key={r.id} className="px-5 py-2.5 border-t border-slate-50 text-xs flex justify-between">
            <span className="font-mono text-slate-600">{r.bundle_number} • {r.product_name} {r.variant}</span>
            <span className="text-slate-500">+{r.qty} pcs • {r.received_by} • {formatDateID(r.created_at)}</span>
          </div>
        ))}
        {!receipts.length && <div className="py-6 text-center text-xs text-slate-400">Belum ada penerimaan.</div>}
      </div>
      {recvFor && (
        <Modal tid="recv-modal" title={`Terima Barang • ${recvFor.number}`} onClose={() => setRecvFor(null)}>
          <p className="text-xs text-slate-500">Konfirmasi qty fisik diterima gudang dari hasil lolos QC ({recvFor.unreceived} pcs belum diterima).</p>
          <div><label className="text-[11px] font-semibold text-slate-500">Qty Diterima</label><input data-testid="recv-qty" type="number" min="1" max={recvFor.unreceived} value={qty} onChange={(e) => setQty(e.target.value)} className={inpL} /></div>
          <button data-testid="recv-submit" onClick={receive} disabled={busy} className="w-full h-11 rounded-xl text-white text-sm font-semibold disabled:opacity-40" style={{ background: MAROON }}>{busy ? "Memproses..." : "Konfirmasi Penerimaan (+Stok)"}</button>
        </Modal>
      )}
    </div>
  );
}

/* ---------------- SCAN & PRINT BUNDEL ---------------- */
function BundleScanInput({ onFound, testid }) {
  const [code, setCode] = useState("");
  const [cam, setCam] = useState(false);
  const videoRef = useRef(null);
  const supported = typeof window !== "undefined" && "BarcodeDetector" in window && !!navigator.mediaDevices;
  useEffect(() => {
    if (!cam) return undefined;
    let stream; let stopped = false; let raf;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        const det = new window.BarcodeDetector({ formats: ["code_128", "qr_code"] });
        const tick = async () => {
          if (stopped) return;
          try {
            const codes = await det.detect(videoRef.current);
            if (codes.length) { onFound(codes[0].rawValue); setCam(false); return; }
          } catch { /* frame tanpa kode */ }
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch { toast.error("Kamera tidak tersedia — gunakan ketik manual"); setCam(false); }
    })();
    return () => { stopped = true; if (raf) cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [cam]);
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input data-testid={`${testid}-code`} value={code} onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && code.trim()) { onFound(code.trim()); setCode(""); } }}
          placeholder="Scan / ketik kode bundel lalu Enter" className={inpL} />
        {supported && <button type="button" data-testid={`${testid}-cam`} onClick={() => setCam(!cam)} className="h-10 px-3 rounded-xl border border-slate-200 text-slate-500 text-xs font-semibold shrink-0">{cam ? "Tutup" : "Kamera"}</button>}
      </div>
      {cam && <video ref={videoRef} data-testid={`${testid}-video`} className="w-full rounded-xl border border-slate-200 max-h-52 object-cover bg-black" muted playsInline />}
    </div>
  );
}

function BatchPrintModal({ batch, onClose }) {
  useEffect(() => {
    (batch.bundles || []).forEach((b, i) => {
      try { JsBarcode(`#pbb-${i}`, b.number, { format: "CODE128", displayValue: false, height: 30, margin: 0, width: 1.2, background: "#ffffff" }); } catch { /* kode tak valid */ }
    });
  }, [batch]);
  return (
    <Modal tid="batch-print-modal" title={`Barcode Bundel — ${batch.number}`} onClose={onClose} xl>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] text-slate-500">{batch.bundles?.length || 0} label Code 128 — tempel di bundel, bisa discan saat Input Selesai Jahit & QC.</p>
        <button data-testid="batch-print-btn" onClick={() => window.print()} className="h-9 px-4 rounded-xl text-white text-xs font-semibold" style={{ background: MAROON }}>Print Label</button>
      </div>
      <div id="bundle-print-area" data-testid="batch-print-area" className="flex flex-wrap gap-2 content-start">
        {(batch.bundles || []).map((b, i) => (
          <div key={b.id} className="border border-slate-300 rounded-lg p-2 w-[180px] text-center bg-white">
            <div className="text-[10px] font-bold text-slate-800 truncate">{b.product_name}</div>
            <div className="text-[9px] text-slate-500">{b.variant} • {b.qty} pcs</div>
            <svg id={`pbb-${i}`} className="mx-auto my-1" />
            <div className="text-[9px] font-mono text-slate-600">{b.number}</div>
          </div>
        ))}
      </div>
      <style>{`@media print { body * { visibility: hidden; } #bundle-print-area, #bundle-print-area * { visibility: visible; } #bundle-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 8px; } }`}</style>
    </Modal>
  );
}

/* ---------------- MONITORING ---------------- */
const MON_TAHAP = [["semua", "Semua Tahap"], ["potong", "Potong"], ["jahit", "Jahit"], ["qc", "Finishing & QC"], ["gudang", "Gudang"], ["selesai", "Selesai"]];
const MON_STATUS = [["semua", "Semua Status"], ["draft", "Draft"], ["berjalan", "Berjalan"], ["diproses", "Diproses"], ["selesai", "Selesai"]];

function monStageOf(m) {
  const p = m.stages.pct;
  if (p.potong < 100) return "potong";
  if (p.jahit < 100) return "jahit";
  if (p.qc < 100) return "qc";
  if (p.gudang < 100) return "gudang";
  return "selesai";
}

function monStatusOf(m) {
  if (m.status === "draft") return { id: "draft", label: "Draft", cls: "bg-slate-100 text-slate-400" };
  if (m.stages.pct.gudang >= 100) return { id: "selesai", label: "Selesai", cls: "bg-emerald-50 text-emerald-600" };
  if (m.stages.potong > 0) return { id: "diproses", label: "Diproses", cls: "bg-amber-100 text-amber-700" };
  return { id: "berjalan", label: "Berjalan", cls: "bg-emerald-50 text-emerald-600" };
}

function MonProgressBar({ pc }) {
  return (
    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5">
      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pc)}%`, background: pc >= 100 ? "#16a34a" : MAROON }} />
    </div>
  );
}

function StageMonitoring({ options, reload, canSpk, canCut }) {
  const [items, setItems] = useState(null);
  const [showSpkForm, setShowSpkForm] = useState(false);
  const [handoverFor, setHandoverFor] = useState(null);
  const [usageFor, setUsageFor] = useState(null);
  const [holdFor, setHoldFor] = useState(null);
  const [q, setQ] = useState("");
  const [prod, setProd] = useState("");
  const [period, setPeriod] = useState("");
  const [tahap, setTahap] = useState("semua");
  const [status, setStatus] = useState("semua");
  const [open, setOpen] = useState({});

  const load = useCallback(() => { api.get("/prod/monitoring").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);
  const saved = () => { load(); reload?.(); };
  const publishSpk = async (m) => {
    try { await api.post(`/admin/prod/spk/${m.id}/publish`); toast.success(`SPK ${m.number} diterbitkan ke tim cutting`); saved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menerbitkan"); }
  };

  const prods = useMemo(() => [...new Set((items || []).map((m) => m.product_name).filter(Boolean))], [items]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (items || []).filter((m) => {
      if (s && !(m.number.toLowerCase().includes(s) || (m.product_name || "").toLowerCase().includes(s))) return false;
      if (prod && m.product_name !== prod) return false;
      if (period && !(m.created_at || "").startsWith(period)) return false;
      if (tahap !== "semua" && monStageOf(m) !== tahap) return false;
      if (status !== "semua" && monStatusOf(m).id !== status) return false;
      return true;
    });
  }, [items, q, prod, period, tahap, status]);
  const { paged: shown, pager: monPager } = usePager(filtered, 10);

  const barsOf = (m) => {
    const st = m.stages, p = st.pct;
    return [
      { icon: Scissors, label: "Potong", val: st.potong, pc: p.potong },
      { icon: Layers, label: "Jahit", val: st.setoran, pc: p.jahit },
      { icon: ClipboardCheck, label: "Finishing & QC", val: st.diperiksa, pc: p.qc },
      { icon: CheckSquare, label: "Lolos QC", val: st.lolos, pc: p.lolos },
      { icon: Archive, label: "Gudang", val: st.diterima, pc: p.gudang },
    ];
  };

  return (
    <div className="space-y-4" data-testid="stage-monitoring">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Monitoring Produksi</h2>
          <p className="text-xs text-slate-400">Pantau progres setiap SPK dari potong sampai gudang.</p>
        </div>
        {canSpk && <button data-testid="mon-spk-create" onClick={() => setShowSpkForm(true)} className="h-10 px-4 rounded-xl text-white text-sm font-semibold flex items-center gap-1.5" style={{ background: MAROON }}><Plus className="w-4 h-4" /> Buat SPK</button>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <div className="relative col-span-2 lg:col-span-1">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="mon-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari SPK atau produk..." className={`${inpL} pl-9`} />
        </div>
        <select data-testid="mon-product" value={prod} onChange={(e) => setProd(e.target.value)} className={inpL}>
          <option value="">Semua Produk</option>
          {prods.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input data-testid="mon-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className={inpL} />
        <select data-testid="mon-stage" value={tahap} onChange={(e) => setTahap(e.target.value)} className={inpL}>
          {MON_TAHAP.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="mon-more" value={status} onChange={(e) => setStatus(e.target.value)} className={inpL}>
          {MON_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {shown.map((m) => {
        const st = m.stages, p = st.pct;
        const statusM = monStatusOf(m);
        const isOpen = !!open[m.id];
        return (
          <div key={m.id} data-testid={`mon-card-${m.id}`} className={`${card} p-5 space-y-4`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-2.5 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display text-base font-bold text-slate-900">{m.number}</span>
                  <span data-testid={`mon-status-${m.id}`} className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusM.cls}`}>{statusM.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  {m.image
                    ? <img src={m.image} alt="" className="w-14 h-14 rounded-xl object-cover border border-slate-100 shrink-0" />
                    : <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0"><Package className="w-5 h-5 text-slate-300" /></div>}
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 text-sm truncate">{m.product_name}</div>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                      <span className="text-[10px] text-slate-400">Varian:</span>
                      {(m.variants || []).map((v) => (
                        <span key={v.variant} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{v.variant} • {v.target_qty.toLocaleString("id-ID")} pcs</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6 flex-wrap">
                <div className="text-left"><div className="text-[10px] uppercase tracking-wide text-slate-400">Target</div><div className="font-display text-sm font-bold text-slate-900">{m.target_qty.toLocaleString("id-ID")} pcs</div></div>
                <div className="text-left"><div className="text-[10px] uppercase tracking-wide text-slate-400">Deadline</div><div className="font-display text-sm font-bold text-slate-900">{m.deadline ? formatDateID(m.deadline) : "—"}</div></div>
                <div className="rounded-xl px-4 py-2.5 bg-rose-50/70 border border-rose-100 min-w-[130px]">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#7F1D2D]"><Package className="w-3 h-3" /> Masuk Gudang</div>
                  <div data-testid={`mon-gudang-pct-${m.id}`} className="font-display text-2xl font-bold text-[#7F1D2D]">{p.gudang}%</div>
                  <div className="text-[10px] text-slate-500">{st.diterima.toLocaleString("id-ID")} dari {m.target_qty.toLocaleString("id-ID")} pcs</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              {barsOf(m).map((b) => (
                <div key={b.label} className="border border-slate-100 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><b.icon className="w-3.5 h-3.5 text-[#7F1D2D]" /> {b.label}</div>
                  <div className="flex items-baseline justify-between mt-1">
                    <span className="text-xs font-bold text-slate-800">{b.val.toLocaleString("id-ID")} pcs</span>
                    <span className={`text-xs font-bold ${b.pc >= 100 ? "text-emerald-600" : "text-[#7F1D2D]"}`}>{b.pc}%</span>
                  </div>
                  <MonProgressBar pc={b.pc} />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 flex-wrap">
              {canSpk && m.status === "draft" && <button data-testid={`mon-publish-${m.id}`} onClick={() => publishSpk(m)} className="h-9 px-4 rounded-xl text-white text-xs font-semibold" style={{ background: MAROON }}>Terbitkan SPK</button>}
              {canSpk && m.status !== "draft" && !m.handover_done && (m.materials || []).length > 0 && <button data-testid={`mon-handover-${m.id}`} onClick={() => setHandoverFor({ id: m.id, number: m.number, materials: m.materials })} className="h-9 px-4 rounded-xl text-white text-xs font-semibold" style={{ background: MAROON }}>Serahkan Bahan</button>}
              {canCut && m.status !== "draft" && m.handover_done && !m.usage_done && <button data-testid={`mon-usage-${m.id}`} onClick={() => setUsageFor({ id: m.id, number: m.number, handover: { materials: m.handover_materials || [] } })} className="h-9 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold">Catat Pemakaian & Sisa</button>}
              {canCut && m.status !== "draft" && m.handover_done && !m.hold_pending && m.stages.potong === 0 && <button data-testid={`mon-hold-${m.id}`} onClick={() => setHoldFor({ id: m.id, number: m.number, target_qty: m.target_qty })} className="h-9 px-4 rounded-xl border border-amber-200 text-amber-600 text-xs font-semibold">Ajukan Hold</button>}
              {!isOpen && <button data-testid={`mon-detail-btn-${m.id}`} onClick={() => setOpen((o) => ({ ...o, [m.id]: true }))} className="h-9 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1.5">Detail Progres <ChevronDown className="w-3.5 h-3.5" /></button>}
            </div>

            {isOpen && (
              <div data-testid={`mon-detail-${m.id}`} className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-display font-bold text-sm text-slate-900">Detail Progres</span>
                  <button data-testid={`mon-detail-close-${m.id}`} onClick={() => setOpen((o) => ({ ...o, [m.id]: false }))} className="text-[11px] font-semibold text-[#7F1D2D] flex items-center gap-1"><ChevronDown className="w-3.5 h-3.5 rotate-180" /> Tutup Detail</button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                  <div className="border border-slate-100 rounded-xl p-3.5 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700"><Scissors className="w-3.5 h-3.5 text-[#7F1D2D]" /> Potong</div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Hasil</span><b>{st.potong.toLocaleString("id-ID")} pcs</b></div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Belum Setor Jahit</span><b>{st.belum_setor.toLocaleString("id-ID")} pcs</b></div>
                  </div>
                  <div className="border border-slate-100 rounded-xl p-3.5 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700"><Layers className="w-3.5 h-3.5 text-[#7F1D2D]" /> Jahit</div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Setoran</span><b>{st.setoran.toLocaleString("id-ID")} pcs</b></div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Belum Diperiksa</span><b>{Math.max(0, st.setoran - st.diperiksa).toLocaleString("id-ID")} pcs</b></div>
                  </div>
                  <div className="border border-slate-100 rounded-xl p-3.5 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700"><ClipboardCheck className="w-3.5 h-3.5 text-[#7F1D2D]" /> Finishing & QC</div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Diperiksa</span><b>{st.diperiksa.toLocaleString("id-ID")} pcs</b></div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Lolos</span><b className="text-emerald-600">{st.lolos.toLocaleString("id-ID")} pcs</b></div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Perbaikan</span><b className="text-amber-600">{st.perbaikan.toLocaleString("id-ID")} pcs</b></div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Reject</span><b className="text-red-500">{st.reject.toLocaleString("id-ID")} pcs</b></div>
                  </div>
                  <div className="border border-slate-100 rounded-xl p-3.5 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700"><Package className="w-3.5 h-3.5 text-[#7F1D2D]" /> Gudang</div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Siap Diserahkan</span><b>{st.siap.toLocaleString("id-ID")} pcs</b></div>
                    <div className="flex justify-between text-[11px]"><span className="text-slate-400">Diterima</span><b className="text-emerald-600">{st.diterima.toLocaleString("id-ID")} pcs</b></div>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Belum setor dapat mencakup pekerjaan yang masih dipegang penjahit.</p>
                <div className="border border-slate-100 rounded-xl overflow-hidden overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-slate-50/60 text-left text-[10px] uppercase text-slate-400">
                      <th className="px-4 py-2">Variasi</th><th className="px-3 py-2 text-right">Target</th><th className="px-3 py-2 text-right">Potong</th>
                      <th className="px-3 py-2 text-right">Jahit</th><th className="px-3 py-2 text-right">Diperiksa QC</th><th className="px-3 py-2 text-right">Lolos QC</th><th className="px-4 py-2 text-right">Gudang</th>
                    </tr></thead>
                    <tbody>
                      {(m.variants || []).map((v) => (
                        <tr key={v.variant} data-testid={`mon-var-${m.id}-${v.variant}`} className="border-t border-slate-50">
                          <td className="px-4 py-2 font-semibold text-slate-700">{v.variant}</td>
                          <td className="px-3 py-2 text-right">{v.target_qty.toLocaleString("id-ID")} pcs</td>
                          <td className="px-3 py-2 text-right">{v.potong.toLocaleString("id-ID")} pcs</td>
                          <td className="px-3 py-2 text-right">{v.setoran.toLocaleString("id-ID")} pcs</td>
                          <td className="px-3 py-2 text-right">{v.diperiksa.toLocaleString("id-ID")} pcs</td>
                          <td className="px-3 py-2 text-right text-emerald-600 font-semibold">{v.lolos.toLocaleString("id-ID")} pcs</td>
                          <td className="px-4 py-2 text-right font-semibold">{v.diterima.toLocaleString("id-ID")} pcs</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {items && !filtered.length && <div data-testid="mon-empty" className={`${card} py-10 text-center text-xs text-slate-400`}>Tidak ada SPK yang cocok dengan filter.</div>}
      {!items && <div className={`${card} py-10 text-center text-xs text-slate-400`}>Memuat...</div>}

      <Pager {...monPager} testid="mon-pagination" />
      {showSpkForm && <SpkForm options={options} onClose={() => setShowSpkForm(false)} onSaved={saved} />}
      {handoverFor && <HandoverModal spk={handoverFor} options={options} onClose={() => setHandoverFor(null)} onSaved={saved} />}
      {usageFor && <UsageModal spk={usageFor} onClose={() => setUsageFor(null)} onSaved={saved} />}
      {holdFor && <HoldModal spk={holdFor} onClose={() => setHoldFor(null)} onSaved={saved} />}
    </div>
  );
}

/* ---------------- MAIN ---------------- */
export default function AdminProduction() {
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const [isMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [stage, setStage] = useState(0);
  const [options, setOptions] = useState({ products: [], materials: [], units: [], employees: [] });
  const [spks, setSpks] = useState([]);
  const [batches, setBatches] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [readyWh, setReadyWh] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [presenceData, setPresenceData] = useState(null);
  const [presenceDate, setPresenceDate] = useState(new Date().toISOString().slice(0, 10));
  const touchX = useRef(null);

  useEffect(() => {
    api.get(`/admin/prod/attendance-summary?date=${presenceDate}`).then((r) => setPresenceData(r.data)).catch(() => setPresenceData(null));
  }, [presenceDate]);

  const canSpk = hasPerm("production.spk"), canCut = hasPerm("production.cutting"), canSew = hasPerm("production.sewing"), canQc = hasPerm("production.qc"), canWh = hasPerm("production.warehouse");

  const reload = useCallback(() => {
    api.get("/prod/options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/prod/spk").then((r) => setSpks(r.data)).catch(() => {});
    api.get("/prod/batches").then((r) => setBatches(r.data)).catch(() => {});
    api.get("/prod/sewing/bundles").then((r) => setBundles(r.data)).catch(() => {});
    api.get("/prod/repairs").then((r) => setRepairs(r.data)).catch(() => {});
    api.get("/prod/warehouse/ready").then((r) => setReadyWh(r.data)).catch(() => {});
    api.get("/prod/warehouse/receipts").then((r) => setReceipts(r.data)).catch(() => {});
    api.get("/prod/submissions/pending-count").then((r) => setPendingCount(r.data.count)).catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const go = (i) => setStage(Math.max(0, Math.min(STAGES.length - 1, i)));
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 60) go(stage + (dx < 0 ? 1 : -1));
    touchX.current = null;
  };

  const PrevIcon = stage > 0 ? STAGES[stage - 1].icon : null;
  const NextIcon = stage < STAGES.length - 1 ? STAGES[stage + 1].icon : null;

  if (isMobile) return <Navigate to="/produksi" replace />;

  return (
    <div data-testid="production-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Produksi</h1>
          <p className="text-xs text-slate-400 mt-0.5">Kelola proses produksi per tahap.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="prod-costs-btn" onClick={() => nav("/admin/production/costs")} className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold">Produk Produksi</button>
          {hasPerm("production.master") && <button data-testid="prod-master-btn" onClick={() => nav("/admin/production/master")} className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold">Master Produksi</button>}
          {hasPerm("production.wages") && <button data-testid="prod-wages-btn" onClick={() => nav("/admin/production/wages")} className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold">Upah Produksi</button>}
          <button data-testid="prod-subs-btn" onClick={() => nav("/admin/production/submissions")} className="h-10 px-4 rounded-xl border border-[#7F1D2D]/40 text-[#7F1D2D] text-xs font-semibold flex items-center gap-1.5 relative">
            <Inbox className="w-4 h-4" /> Pengajuan Pengawas
            {pendingCount > 0 && <span data-testid="prod-subs-badge" className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-[#7F1D2D] text-white text-[10px] font-bold flex items-center justify-center">{pendingCount}</span>}
          </button>
        </div>
      </div>

      <div data-testid="presence-card" className={`${card} px-4 py-2.5 flex items-center gap-3 flex-wrap`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><CheckSquare className="w-3.5 h-3.5" /></span>
          <div className="min-w-0">
            <div className="text-xs font-bold text-slate-800 leading-tight">Kehadiran Produksi</div>
            <div className="text-[9px] text-slate-400">Tercatat otomatis saat karyawan buka aplikasi di titik absen</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          {[
            { key: "hadir", label: "Hadir", cls: "bg-emerald-50 text-emerald-600", list: presenceData?.hadir || [] },
            { key: "izin", label: "Izin", cls: "bg-amber-50 text-amber-600", list: presenceData?.izin || [] },
            { key: "alfa", label: "Alfa", cls: "bg-red-50 text-red-500", list: presenceData?.alfa || [] },
          ].map((g) => (
            <span key={g.key} data-testid={`presence-${g.key}`} title={g.list.map((e) => `${e.name} • ${e.role_label}${e.location_name ? ` • ${e.location_name}` : ""}`).join("\n") || "Tidak ada"}
              className={`text-[10px] px-2.5 py-1 rounded-full font-semibold cursor-default ${g.cls}`}>
              {g.label} <b>{g.list.length}</b>
            </span>
          ))}
          <input data-testid="presence-date" type="date" value={presenceDate} onChange={(e) => setPresenceDate(e.target.value)} className="h-8 px-2.5 rounded-lg border border-slate-200 text-[11px]" />
        </div>
      </div>

      {/* Tab bar tahap — klik atau geser */}
      <div className={`${card} flex items-center px-2`}>
        <button data-testid="stage-prev" onClick={() => go(stage - 1)} disabled={stage === 0} className="p-2 text-slate-400 disabled:opacity-20"><ChevronLeft className="w-4 h-4" /></button>
        <div className="flex-1 flex overflow-x-auto">
          {STAGES.map((s, i) => (
            <button key={s.id} data-testid={`stage-tab-${s.id}`} onClick={() => go(i)}
              className={`flex-1 min-w-[120px] flex items-center justify-center gap-1.5 py-3.5 text-xs font-semibold border-b-2 whitespace-nowrap transition-colors ${i === stage ? "border-[#7F1D2D] text-[#7F1D2D]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              <s.icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </div>
        <button data-testid="stage-next" onClick={() => go(stage + 1)} disabled={stage === STAGES.length - 1} className="p-2 text-slate-400 disabled:opacity-20"><ChevronRight className="w-4 h-4" /></button>
      </div>

      {/* Satu tahap per layar — navigasi bebas tanpa menyelesaikan tahap sebelumnya */}
      <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} className="min-h-[400px]">
        {stage === 0 && <StageMonitoring options={options} reload={reload} canSpk={canSpk} canCut={canCut} />}
        {stage === 1 && <StageCutting spks={spks} batches={batches} reload={reload} canCut={canCut} />}
        {stage === 2 && <StageSewing bundles={bundles} employees={options.employees} reload={reload} canSew={canSew} />}
        {stage === 3 && <StageQc bundles={bundles} repairs={repairs} employees={options.employees} reload={reload} canQc={canQc} />}
        {stage === 4 && <StageWarehouse ready={readyWh} receipts={receipts} batches={batches} reload={reload} canWarehouse={canWh} />}
      </div>

      <div className="flex items-center justify-between">
        {stage > 0 ? (
          <button data-testid="stage-goto-prev" onClick={() => go(stage - 1)} className="h-11 px-5 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1.5">
            <ChevronLeft className="w-4 h-4" /> Sebelumnya: {STAGES[stage - 1].label}
          </button>
        ) : <span />}
        {stage < STAGES.length - 1 && (
          <button data-testid="stage-goto-next" onClick={() => go(stage + 1)} className="h-11 px-5 rounded-xl text-white text-xs font-semibold flex items-center gap-1.5" style={{ background: MAROON }}>
            Berikutnya: {STAGES[stage + 1].label} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
